import {StructuredRemarksView} from './StructuredRemarksView'
import React, { FC, useEffect, useMemo, useRef, useState } from 'react'
import { useAuth } from '../../auth'
import { toast } from 'react-toastify'
import { KTSVG } from '../../../../_metronic/helpers'
import { getCompanyList, getCompanyAdminList } from '../../Management/core/_requests'
import type { Vessel, Company, CompanyAdmin } from '../../Management/core/_models'
import DeleteMainEngineReportModal from './DeleteMainEngineReportModal'
import MainEngineActionConfirmModal from './MainEngineActionConfirmModal'
import MainEngineReviewModal from './MainEngineReviewModal'
import { useMonthEndFeatureAccess } from '../core/monthEndAccess'
import QhseAccessDenied from './QhseAccessDenied'
import SearchableSelect from '../../../shared/components/SearchableSelect'
import { useGlobalFilter } from '../../../shared/context/GlobalFilterContext'
import { TableSkeleton } from '../../../shared/components/LoadingSkeletons'
import {
  fetchAccessibleVesselsForCurrentUser,
  formatAccessibleVesselError,
  NO_ACCESSIBLE_VESSELS_MESSAGE,
  normalizeAccessibleVessels,
} from '../../../shared/access/accessibleVessels'

const pickApiErrorMessage = (_err: any, fallback: string) => {
  return fallback
}

const currentYear = new Date().getFullYear()
const currentMonth = String(new Date().getMonth() + 1).padStart(2, '0')

type ModalMode = 'add' | 'edit' | 'view' | null
const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const recordMonthKey = (r: any): string => {
  const y = r?.reportYear
  const m = r?.reportMonth
  if (!y || !m) return ''
  return `${y}-${String(m).padStart(2, '0')}`
}

const monthKeyToParts = (monthKey: string): { reportYear: number; reportMonth: number } | null => {
  if (!monthKey) return null
  const [y, m] = monthKey.split('-').map(Number)
  if (!y || !m) return null
  return { reportYear: y, reportMonth: m }
}

const monthKeyToLabel = (monthKey: string): string => {
  const parts = monthKeyToParts(monthKey)
  if (!parts) return ''
  return `${MONTHS_SHORT[parts.reportMonth - 1] ?? parts.reportMonth} ${parts.reportYear}`
}

const buildMonthRange = (fromKey: string, toKey: string): string[] => {
  if (!fromKey || !toKey) return []
  const [fy, fm] = fromKey.split('-').map(Number)
  const [ty, tm] = toKey.split('-').map(Number)
  if (!fy || !fm || !ty || !tm) return []
  let start = fy * 12 + (fm - 1)
  let end = ty * 12 + (tm - 1)
  if (start > end) [start, end] = [end, start]
  const out: string[] = []
  for (let cur = start; cur <= end; cur++) {
    const y = Math.floor(cur / 12)
    const m = (cur % 12) + 1
    out.push(`${y}-${String(m).padStart(2, '0')}`)
  }
  return out
}
const BOILER_LINKED_REPLICA_FIELDS = [
  'boilerMakeTypeCapacity',
  'boiler1PerformanceTestDatePressure',
  'lastDateBoiler1InternalInspection',
  'quantityPluggedBoiler1Pipes',
  'dateOfLastBoilerWaterAnalysis',
  'waterAnalysisOk',
  'enoughStockOfChemicalForAnalysis',
] as const
const IGS_LINKED_REPLICA_FIELDS = [
  'igIgsMakeTypeCapacity',
  'igIgsPerformanceTestDatePassed',
  'dateOfLastSensorsCalibration',
  'lastTimeDemisterCleaned',
  'dateOfLastShoreServiceAttendance',
  'dateOfLastInternalInspection',
  'dateOfLastWaterNodesCheck',
] as const
const BWTS_LINKED_REPLICA_FIELDS = [
  'bwtsMakeTypeCapacity',
  'bwtsOperationalTestDatePassed',
  'minStockCriticalSparePartsChecked',
  'dateOfLastShoreServiceAttendance',
] as const
const FWG_LINKED_REPLICA_FIELDS = [
  'fwgMakeTypeCapacity',
  'dateOfLastMaintenance',
] as const
const BOILER_LABELED_REPLICA_FIELDS = [
  'boilerMakeTypeCapacity',
  'boiler1PerformanceTestDatePressure',
  'lastDateBoiler1InternalInspection',
  'quantityPluggedBoiler1Pipes',
] as const
const AUX_ENGINE_LINKED_REPLICA_FIELDS = ['aeMakeTypeKw', 'dailyConsumptionVlsfoMgo', 'ae1MaxLoad'] as const
const COMPRESSOR_LINKED_REPLICA_FIELDS = [
  'mac1MakeModelCapacity',
  'mac1GoodWorkingCondition',
  'mac1PmsSparePartAvailable',
] as const
const PURIFIER_LINKED_REPLICA_FIELDS = [
  'hfoPurifier1MakeModel',
  'hfoPurifier1GoodWorkingCondition',
  'hfoPurifier1PmsSparePartAvailable',
] as const
const LABELED_REPLICA_FIELDS = [
  'ae1MaxLoad',
  'aeMakeTypeKw',
  'mac1MakeModelCapacity',
  'hfoPurifier1MakeModel',
  'fwgMakeTypeCapacity',
  'bwtsMakeTypeCapacity',
  'igIgsMakeTypeCapacity',
  'igIgsPerformanceTestDatePassed',
  'boilerMakeTypeCapacity',
  ...BOILER_LINKED_REPLICA_FIELDS,
] as const

const getAuxEngineReplicaLabel = (index: number) => `Auxiliary Engine Make / Type / KW ${index + 2}`
const getBoilerReplicaLabel = (fieldKey: string, index: number) => {
  const labels: Record<string, string> = {
    boilerMakeTypeCapacity: `Boiler Make / Type / Capacity ${index + 2}`,
    boiler1PerformanceTestDatePressure: `Boiler #1 Performance Test date / Pressure ${index + 2}`,
    dateOfLastBoilerWaterAnalysis: `Date of Last Boiler Water Analysis ${index + 2}`,
    waterAnalysisOk: `Water Analysis OK (Yes/Not), if Not corrective action ${index + 2}`,
    enoughStockOfChemicalForAnalysis: `Enough Stock of Chemical for Analysis (Yes/Not) ${index + 2}`,
    lastDateBoiler1InternalInspection: `Last Date of Boiler #1 Internal Inspection ${index + 2}`,
    quantityPluggedBoiler1Pipes: `Quantity of Plugged Boiler #1 Pipes ${index + 2}`,
  }
  return labels[fieldKey] || `Boiler ${index + 2}`
}
const getIgsReplicaLabel = (index: number) => `IG / IGS Make / Type / Capacity ${index + 2}`
const getBwtsReplicaLabel = (index: number) => `BWTS Make / Type / Capacity ${index + 2}`
const getCompressorReplicaLabel = (index: number) => `MAC #1 - Make / Model / Capacity ${index + 2}`
const getPurifierReplicaLabel = (index: number) => `HFO Purifier #1 - Make / Model ${index + 2}`
const getFwgReplicaLabel = (fieldKey: string, index: number) => {
  const labels: Record<string, string> = {
    fwgMakeTypeCapacity: `FWG Make / Type / Capacity ${index + 2}`,
    dateOfLastMaintenance: `Date of Last Maintenance ${index + 2}`,
  }
  return labels[fieldKey] || `FWG ${index + 2}`
}
const isAuxEngineReplicaField = (fieldKey: string) =>
  (AUX_ENGINE_LINKED_REPLICA_FIELDS as readonly string[]).includes(fieldKey)
const isIgsReplicaField = (fieldKey: string) =>
  (['igIgsMakeTypeCapacity', 'igIgsPerformanceTestDatePassed'] as const).includes(
    fieldKey as any
  )
const isFwgReplicaField = (fieldKey: string) =>
  (FWG_LINKED_REPLICA_FIELDS as readonly string[]).includes(fieldKey as any)

export type FieldType = 'text' | 'number' | 'date' | 'select' | 'textarea'

export type ColumnDef = {
  key: string
  header: string
  render?: (row: any) => React.ReactNode
}

export type FormFieldDef = {
  key: string
  label: string
  type: FieldType
  options?: { value: string; label: string }[]
  colSpan?: number
  section?: string
  /** If true, a simple "Add" button renders next to this field's label so the user can add more instances of JUST this field. */
  replicatable?: boolean
  /** Custom label for the replicator button (e.g. "Add Make"). Defaults to "Add". */
  replicateButtonLabel?: string
}

export type RepeatableEntryFieldDef = {
  key: string
  label: string
  type: FieldType
  options?: { value: string; label: string }[]
  colSpan?: number
}

export type RepeatableSectionConfig = {
  sectionId: string
  /** Optional: fixed formField section name this section belongs to. Currently informational; entries render at the bottom of the modal. */
  anchorSection?: string
  title?: string
  description?: string
  addButtonLabel: string
  itemLabelPrefix: string
  startIndex: number
  fields: RepeatableEntryFieldDef[]
}

export type MachineryReportConfig = {
  reportKey: string
  title: string
  subtitle: string
  columns: ColumnDef[]
  formFields: FormFieldDef[]
  carryForwardFields?: string[]
  carryForwardReplicaLabels?: string[]
  emptyForm: () => Record<string, any> & {
    vesselId?: number | null
    reportMonth?: number | null
    reportYear?: number | null
    _makeReplicas?: Record<string, any[]>
    _extraEntries?: Record<string, Record<string, any>[]>
    _reportKey?: string
  }
  searchFn: (params: any) => Promise<any[]>
  createFn: (dto: any) => Promise<any>
  updateFn: (id: number, dto: any) => Promise<any>
  deleteFn: (id: number) => Promise<void>
  defaultsFn?: (vesselId: number) => Promise<any>
  repeatableSections?: RepeatableSectionConfig[]
}

type MachineryReportRegistryItem = {
  key: string
  searchFn: (params: any) => Promise<any[]>
  createFn?: (dto: any) => Promise<any>
  updateFn?: (id: number, dto: any) => Promise<any>
  defaultsFn?: (vesselId: number) => Promise<any>
  emptyForm?: () => Record<string, any> & {
    vesselId?: number | null
    reportMonth?: number | null
    reportYear?: number | null
    _makeReplicas?: Record<string, any[]>
    _extraEntries?: Record<string, Record<string, any>[]>
    _reportKey?: string
  }
  repeatableSections?: RepeatableSectionConfig[]
  title?: string
  carryForwardFields?: string[]
  carryForwardReplicaLabels?: string[]
}

type Props = {
  config: MachineryReportConfig
  modalConfig?: MachineryReportConfig
  showAddButton?: boolean
  addRequestId?: number
  addRequestSeed?: { vesselId?: number | null; reportYear?: number | null; reportMonth?: number | null }
  modalSwitchSeed?: { vesselId?: number | null; reportYear?: number | null; reportMonth?: number | null }
  activeReportKey?: string
  addReportOptions?: { key: string; label: string; icon: string }[]
  onSwitchAddReport?: (
    reportKey: string,
    seed?: { vesselId?: number | null; reportYear?: number | null; reportMonth?: number | null }
  ) => void
  onRequestModalReportKey?: (reportKey: string) => void
  reportRegistry?: MachineryReportRegistryItem[]
  onDownloadPdf?: (row: any) => void
}

const MachineryReportTable: FC<Props> = ({
  config,
  modalConfig: modalConfigProp,
  showAddButton = true,
  addRequestId = 0,
  addRequestSeed,
  modalSwitchSeed,
  activeReportKey = '',
  addReportOptions = [],
  onSwitchAddReport,
  onRequestModalReportKey,
  reportRegistry = [],
  onDownloadPdf,
}) => {
  const modalConfig = modalConfigProp ?? config
  const { currentUser } = useAuth()
  const {
    canRead,
    canCreate,
    canUpdate,
    canDelete,
    hasAssignedVessel,
    assignedCompanyGroupId,
    assignedCompanyAdminId,
    isGlobalScope,
    isOfficeUser,
  } = useMonthEndFeatureAccess('machinery-status')
  const isShipboardUser = hasAssignedVessel

  const canMultiMonthForShipboard = isShipboardUser && (canCreate || canUpdate)
  const useMultiSelectFilters = isOfficeUser || canMultiMonthForShipboard
  const useMultiMonthFilters = isOfficeUser || canMultiMonthForShipboard
  const isRestrictedOfficeUser = isOfficeUser && !isGlobalScope

  const canAdd = canCreate
  const canEditRow = (r: any) =>
    canUpdate && (isOfficeUser || (r.status === 'DRAFT' || !r.status))
  const canDeleteRow = (r: any) =>
    canDelete &&
    r.status !== 'REVIEWED' &&
    (isOfficeUser || r.status === 'DRAFT' || !r.status)
  const canSubmitRow = (r: any) =>
    canUpdate && (isShipboardUser || isGlobalScope) && (r.status === 'DRAFT' || !r.status)
  const canReviewRow = (r: any) => canUpdate && isOfficeUser && r.status === 'SUBMITTED'
  const canDownloadPdf = (r: any) => r.status === 'REVIEWED' && onDownloadPdf
  const isContinuationPlaceholder = (r: any) => Boolean(r?.__isContinuationPlaceholder)
  const isAuxiliaryReport = config.reportKey === 'auxiliary-engine'
  const accessDenied = !canRead

  /* if (!canRead) {
    return (
      <QhseAccessDenied
        message={`Access denied — you do not have permission to view ${config.title}.`}
      />
    )
  } */

  const [vessels, setVessels] = useState<Vessel[]>([])
  const [companyAdmins, setCompanyAdmins] = useState<CompanyAdmin[]>([])
  const [subCompanies, setSubCompanies] = useState<Company[]>([])
  const [records, setRecords] = useState<any[]>([])
  const [continuationRows, setContinuationRows] = useState<any[]>([])

  const [filterVessel, setFilterVessel] = useState<string>('')
  const [filterVesselIds, setFilterVesselIds] = useState<number[]>([])
  const [filterMonthKeys, setFilterMonthKeys] = useState<string[]>([])
  const [compareViewYear, setCompareViewYear] = useState<number>(new Date().getFullYear())
  const [filterMonthFrom, setFilterMonthFrom] = useState<string>('')
  const [filterMonthTo, setFilterMonthTo] = useState<string>('')

  const [compareMonthDropdownOpen, setCompareMonthDropdownOpen] = useState(false)
  const [vesselDropdownOpen, setVesselDropdownOpen] = useState(false)
  const [monthDropdownOpen, setMonthDropdownOpen] = useState(false)
  const [filterCompanyGroup, setFilterCompanyGroup] = useState<string>('')
  const [filterCompanyAdmin, setFilterCompanyAdmin] = useState<string>('')
  const [filterMonthValue, setFilterMonthValue] = useState<string>(
    isGlobalScope ? '' : `${currentYear}-${currentMonth}`
  )
  const selectedFilterMonthKey = useMultiMonthFilters
    ? filterMonthKeys.length === 1
      ? filterMonthKeys[0]
      : ''
    : filterMonthValue
  const [modalCompanyGroup, setModalCompanyGroup] = useState<string>('')
  const [modalCompanyAdmin, setModalCompanyAdmin] = useState<string>('')

  const { state: globalState } = useGlobalFilter()

  useEffect(() => {
    if (globalState.vessel?.id) {
      setFilterVessel(String(globalState.vessel.id))
      setFilterVesselIds([Number(globalState.vessel.id)])
    } else {
      setFilterVessel('')
      setFilterVesselIds([])
    }

    if (globalState.company?.id) {
      setFilterCompanyGroup(String(globalState.company.id))
    } else {
      setFilterCompanyGroup('')
    }
  }, [globalState.vessel, globalState.company])

  const [modalMode, setModalMode] = useState<ModalMode>(null)
  const [form, setForm] = useState<Record<string, any>>(config.emptyForm())
  const [wizardData, setWizardData] = useState<Record<string, any>>({})
  const [editId, setEditId] = useState<number | null>(null)
  const [deletingRecord, setDeletingRecord] = useState<any | null>(null)
  const [submitTargetId, setSubmitTargetId] = useState<number | null>(null)
  const [reviewingRecord, setReviewingRecord] = useState<any | null>(null)
  const [isReviewSaving, setIsReviewSaving] = useState(false)
  const [loading, setLoading] = useState(true)
  const [wizardLoading, setWizardLoading] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const mounted = useRef(true)
  const modalReportKeyRef = useRef(modalConfig.reportKey)

  const selectedMonthSummary = useMemo(
    () =>
      useMultiMonthFilters
        ? filterMonthKeys.slice().sort().map((monthKey) => monthKeyToLabel(monthKey)).filter(Boolean).join(', ')
        : filterMonthValue
          ? monthKeyToLabel(filterMonthValue)
          : '',
    [filterMonthKeys, filterMonthValue, useMultiMonthFilters]
  )
  const selectedVesselSummary = useMemo(() => {
    const selectedVessels = useMultiSelectFilters
      ? vessels.filter((v) => filterVesselIds.includes(Number(v.id)))
      : filterVessel
        ? vessels.filter((v) => String(v.id) === filterVessel)
        : []

    return selectedVessels
      .map((v) => v.fleet_name || v.name || '')
      .filter(Boolean)
      .join(', ')
  }, [filterVessel, filterVesselIds, useMultiSelectFilters, vessels])
  const filterSummaryMessage =
    selectedMonthSummary && selectedVesselSummary
      ? `You are filling for ${selectedMonthSummary} for ${selectedVesselSummary}.`
      : ''

  const handleVesselMultiSelectChange = (nextVesselIds: number[]) => {
    if (nextVesselIds.length > 1 && filterMonthKeys.length > 1) {
      setFilterMonthKeys((prev) => prev.slice(0, 1))
      toast.info('Only one month can be selected when multiple vessels are selected.', {
        position: 'top-center',
      })
    }
    setFilterVesselIds(nextVesselIds)
  }

  const handleMonthMultiSelectChange = (nextMonthKeys: string[]) => {
    if (nextMonthKeys.length > 1 && filterVesselIds.length > 1) {
      setFilterVesselIds((prev) => prev.slice(0, 1))
      toast.info('Only one vessel can be selected when multiple months are selected.', {
        position: 'top-center',
      })
    }
    setFilterMonthKeys(nextMonthKeys)
  }

  const resolvePreferredMonthKey = async (
    vesselId: number | null,
    seed?: { reportYear?: number | null; reportMonth?: number | null }
  ) => {
    const seedKey =
      seed?.reportYear && seed?.reportMonth
        ? `${seed.reportYear}-${String(seed.reportMonth).padStart(2, '0')}`
        : ''
    if (seedKey) return seedKey

    // Query the current report type's latest record to find the preferred month
    if (config.searchFn && vesselId) {
      try {
        const params: Record<string, any> = { vesselId }
        if (filterCompanyGroup) params.companyGroupId = Number(filterCompanyGroup)
        if (filterCompanyAdmin) params.companyAdminId = Number(filterCompanyAdmin)
        const currentRecords = await config.searchFn(params)
        const recordList = Array.isArray(currentRecords) ? currentRecords : []
        const latestRecord = recordList
          .filter((r) => Number(r?.vesselId) === vesselId)
          .sort((a, b) => {
            const aKey = recordMonthKey(a)
            const bKey = recordMonthKey(b)
            if (aKey !== bKey) return aKey < bKey ? 1 : -1
            return Number(b?.id ?? 0) - Number(a?.id ?? 0)
          })[0]
        const latestKey = recordMonthKey(latestRecord)
        if (latestKey) return latestKey
      } catch {
        // Best-effort only; fallback below.
      }
    }

    return selectedFilterMonthKey || `${currentYear}-${currentMonth}`
  }



  useEffect(() => {
    return () => {
      mounted.current = false
    }
  }, [])

  useEffect(() => {
    if (!isGlobalScope) return
    const loadCompanies = async () => {
      try {
        const [cga, ca] = await Promise.all([getCompanyAdminList(), getCompanyList()])
        if (mounted.current) {
          setCompanyAdmins(cga)
          setSubCompanies(ca)
        }
      } catch (err) {
        console.error('Failed to load companies', err)
      }
    }
    loadCompanies()
  }, [isGlobalScope])

  useEffect(() => {
    const load = async () => {
      try {
        const list = normalizeAccessibleVessels(await fetchAccessibleVesselsForCurrentUser())
        if (!mounted.current) return
        if (isShipboardUser) {
          const myVId = currentUser?.vessel?.id
          const accessibleVesselId = myVId && list.some((v) => Number(v.id) === Number(myVId)) ? String(myVId) : ''
          setVessels(list)
          if (accessibleVesselId) setFilterVessel(accessibleVesselId)
        } else {
          let filtered = list.filter((v: Vessel) => v.active ?? true)
          if (!isGlobalScope) {
            if (assignedCompanyGroupId) {
              filtered = filtered.filter((v) => Number(v.companyGroupAdmin?.id) === Number(assignedCompanyGroupId))
            }
            if (assignedCompanyAdminId) {
              filtered = filtered.filter((v) => Number(v.companyAdmin?.id) === Number(assignedCompanyAdminId))
            }
          }
          if (filterCompanyGroup) {
            filtered = filtered.filter(
              (v) => Number(v.companyGroupAdmin?.id) === Number(filterCompanyGroup)
            )
          }
          if (filterCompanyAdmin) {
            filtered = filtered.filter(
              (v) => Number(v.companyAdmin?.id) === Number(filterCompanyAdmin)
            )
          }
          setVessels(filtered)
        }
      } catch (error) {
        setVessels([])
        toast.error(formatAccessibleVesselError(error, 'Failed to load vessel list'), {
          position: 'top-center',
        })
      }
    }
    load()
  }, [isShipboardUser, isGlobalScope, assignedCompanyGroupId, assignedCompanyAdminId, currentUser, filterCompanyGroup, filterCompanyAdmin])

  useEffect(() => {
    if (useMultiSelectFilters) {
      setFilterVesselIds((prev) => prev.filter((id) => vessels.some((v) => Number(v.id) === Number(id))))
      return
    }
    if (filterVessel && !vessels.some((v) => String(v.id) === filterVessel)) {
      setFilterVessel('')
    }
  }, [filterVessel, useMultiSelectFilters, vessels])

  useEffect(() => {
    if (isGlobalScope && filterMonthValue) {
      setFilterMonthValue('')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGlobalScope])

  const loadRecords = async () => {
    try {
      setLoading(true)
      const params: any = {}
      if (isShipboardUser) {
        params.vesselId = Number(currentUser?.vessel?.id)
      } else if (!useMultiSelectFilters && filterVessel) {
        params.vesselId = Number(filterVessel)
      }
      if (filterCompanyGroup) params.companyGroupId = Number(filterCompanyGroup)
      if (filterCompanyAdmin) params.companyAdminId = Number(filterCompanyAdmin)
      if (!isGlobalScope) {
        if (assignedCompanyGroupId) params.companyGroupId = Number(assignedCompanyGroupId)
        if (assignedCompanyAdminId) params.companyAdminId = Number(assignedCompanyAdminId)
      }
      if (selectedFilterMonthKey) {
        const [y, m] = selectedFilterMonthKey.split('-')
        params.year = Number(y)
        params.month = Number(m)
      }
      const data = await config.searchFn(params)
      const currentRecords = Array.isArray(data) ? data : []

      const peerConfigs = reportRegistry.filter((r) => r.key !== config.reportKey)
      const peerResults = await Promise.all(
        peerConfigs.map(async (item) => {
          try {
            const result = await item.searchFn(params)
            return Array.isArray(result) ? result : []
          } catch {
            return []
          }
        })
      )

      const existingKeys = new Set(
        currentRecords.map((r) => `${Number(r?.vesselId ?? 0)}::${Number(r?.reportYear ?? 0)}::${Number(r?.reportMonth ?? 0)}`)
      )

      const placeholderMap = new Map<string, any>()
      peerResults.flat().forEach((r: any) => {
        const vesselId = Number(r?.vesselId ?? 0)
        const reportYear = Number(r?.reportYear ?? 0)
        const reportMonth = Number(r?.reportMonth ?? 0)
        if (!vesselId || !reportYear || !reportMonth) return

        const compositeKey = `${vesselId}::${reportYear}::${reportMonth}`
        if (existingKeys.has(compositeKey) || placeholderMap.has(compositeKey)) return

        placeholderMap.set(compositeKey, {
          id: `placeholder-${config.reportKey}-${compositeKey}`,
          vesselId,
          vesselName: r?.vesselName || '',
          reportYear,
          reportMonth,
          status: 'DRAFT',
          __isContinuationPlaceholder: true,
        })
      })

      if (mounted.current) {
        setRecords(currentRecords)
        setContinuationRows(Array.from(placeholderMap.values()))
      }
    } catch {
      if (mounted.current) {
        setRecords([])
        setContinuationRows([])
      }
      toast.error('Failed to load reports', { position: 'top-center' })
    } finally {
      if (mounted.current) setLoading(false)
    }
  }

  useEffect(() => {
    // Reset pagination and reload records when switching tabs or filters
    setCurrentPage(1)
    // Only reset the form when the modal is closed – prevents wiping wizard data
    // during tab switches that change config.reportKey while the Add modal is open
    if (!modalMode) {
      setForm(config.emptyForm())
    }
    loadRecords()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filterVessel,
    filterMonthValue,
    filterCompanyGroup,
    filterCompanyAdmin,
    config.reportKey,
    useMultiSelectFilters,
  ])

  useEffect(() => {
    if (!showAddButton && addRequestId > 0 && canAdd) {
      void handleOpenAdd(addRequestSeed)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addRequestId, addRequestSeed, showAddButton, canAdd])

  useEffect(() => {
    const previousReportKey = modalReportKeyRef.current
    const nextReportKey = modalConfig.reportKey
    modalReportKeyRef.current = nextReportKey

    if (previousReportKey === nextReportKey || !modalMode) return

    if (modalMode === 'add') {
      const cached = wizardData[nextReportKey]
      if (cached) {
        setForm(cached)
      } else {
        const emptyFormFn = modalConfig.emptyForm || (() => ({}))
        const f = emptyFormFn()
        f.vesselId = form.vesselId
        f.reportMonth = form.reportMonth
        f.reportYear = form.reportYear
        f._reportKey = nextReportKey
        f._makeReplicas = {}
        f._extraEntries = {}
        setForm(f)
      }
      return
    }

    const currentVesselId =
      modalSwitchSeed?.vesselId != null
        ? Number(modalSwitchSeed.vesselId)
        : form.vesselId != null
          ? Number(form.vesselId)
          : null
    const currentReportMonth =
      modalSwitchSeed?.reportMonth != null
        ? Number(modalSwitchSeed.reportMonth)
        : form.reportMonth != null
          ? Number(form.reportMonth)
          : null
    const currentReportYear =
      modalSwitchSeed?.reportYear != null
        ? Number(modalSwitchSeed.reportYear)
        : form.reportYear != null
          ? Number(form.reportYear)
          : null

    const switchModalReport = async () => {
      if (currentVesselId && currentReportMonth && currentReportYear) {
        try {
          const existing = await modalConfig.searchFn({
            vesselId: currentVesselId,
            month: currentReportMonth,
            year: currentReportYear,
            activeOnly: true,
          })

          if (existing.length > 0) {
            const record = existing[0]
            const parsed = parseExtraEntries(record.extraEntriesJson)
            setForm({ ...record, _extraEntries: parsed.sections, _makeReplicas: parsed.makeReplicas })
            setEditId(Number(record.id))
            setModalMode('edit')
            return
          }
        } catch {
          // If the lookup fails, fall back to add mode for the selected report type.
        }
      }

      await handleOpenAdd({
        vesselId: currentVesselId,
        reportYear: currentReportYear,
        reportMonth: currentReportMonth,
      })
    }

    void switchModalReport()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalConfig.reportKey, modalSwitchSeed])

  const getModalFilteredVessels = (companyGroupId: string, companyAdminId: string) =>
    vessels.filter((v: Vessel) => {
      if (companyGroupId && Number(v.companyGroupAdmin?.id) !== Number(companyGroupId)) return false
      if (companyAdminId && Number(v.companyAdmin?.id) !== Number(companyAdminId)) return false
      return true
    })

  const modalVessels = useMemo(() => {
    if (!(isGlobalScope && modalMode === 'add')) return vessels
    return getModalFilteredVessels(modalCompanyGroup, modalCompanyAdmin)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGlobalScope, modalMode, vessels, modalCompanyGroup, modalCompanyAdmin])

  const filterSubCompanies = useMemo(
    () =>
      subCompanies.filter(
        (sc) => !filterCompanyGroup || Number(sc.cga?.id) === Number(filterCompanyGroup)
      ),
    [subCompanies, filterCompanyGroup]
  )
  const showFilterSubCompany = !filterCompanyGroup || filterSubCompanies.length > 0

  const modalSubCompanies = useMemo(
    () =>
      subCompanies.filter(
        (sc) => !modalCompanyGroup || Number(sc.cga?.id) === Number(modalCompanyGroup)
      ),
    [subCompanies, modalCompanyGroup]
  )
  const showModalSubCompany = !modalCompanyGroup || modalSubCompanies.length > 0

  const handleCloseReviewModal = () => setReviewingRecord(null)

  const handleSaveReviewOnly = async (officeRemarks: string) => {
    if (!reviewingRecord?.id) return
    try {
      setIsReviewSaving(true)
      await config.updateFn(Number(reviewingRecord.id), { officeRemarks: officeRemarks.trim() })
      toast.success('Office review saved')
      handleCloseReviewModal()
      loadRecords()
    } catch (err) {
      toast.error(pickApiErrorMessage(err, 'Failed to save office review'))
    } finally {
      setIsReviewSaving(false)
    }
  }

  const handleCloseSatisfactory = async (officeRemarks: string) => {
    if (!reviewingRecord?.id) return
    try {
      setIsReviewSaving(true)
      await config.updateFn(Number(reviewingRecord.id), {
        officeRemarks: officeRemarks.trim(),
        status: 'REVIEWED',
      })
      toast.success('Report closed satisfactorily')
      handleCloseReviewModal()
      loadRecords()
    } catch (err) {
      toast.error(pickApiErrorMessage(err, 'Failed to close report satisfactorily'))
    } finally {
      setIsReviewSaving(false)
    }
  }

  const confirmSubmit = async () => {
    if (!submitTargetId) return
    const targetRecord = records.find((record) => Number(record?.id) === Number(submitTargetId))
    if (!targetRecord) {
      toast.error('Unable to find the report you are trying to submit')
      return
    }

    const vesselId = Number(targetRecord?.vesselId ?? 0)
    const reportMonth = Number(targetRecord?.reportMonth ?? 0)
    const reportYear = Number(targetRecord?.reportYear ?? 0)

    if (!vesselId || !reportMonth || !reportYear) {
      toast.error('Report month and vessel are required before submission')
      return
    }

    if (reportRegistry.length > 0) {
      try {
        const results = await Promise.all(
          reportRegistry.map(async (report) => {
            try {
              const rows = await report.searchFn({
                vesselId,
                month: reportMonth,
                year: reportYear,
                activeOnly: true,
              })
              const existingRecord = rows.length > 0 ? rows[0] : null
              return { key: report.key, existingRecord, updateFn: report.updateFn }
            } catch {
              return { key: report.key, existingRecord: null, updateFn: report.updateFn }
            }
          })
        )

        const missingReports = results
          .filter((result) => !result.existingRecord)
          .map((result) => addReportOptions.find((option) => option.key === result.key)?.label ?? result.key)

        if (missingReports.length > 0) {
          toast.error(
            `Fill all machinery status reports for ${monthKeyToLabel(recordMonthKey(targetRecord))} before submitting. Missing: ${missingReports.join(', ')}`
          )
          return
        }

        await Promise.all(
          results.map(async (res) => {
            if (res.existingRecord && res.existingRecord.id && res.updateFn) {
              await res.updateFn(Number(res.existingRecord.id), { status: 'SUBMITTED' })
            }
          })
        )
        toast.success('All machinery reports submitted to Office')
        setSubmitTargetId(null)
        loadRecords()
        return
      } catch (err) {
        console.error("Submission failed", err)
        toast.error('Unable to submit machinery status reports for this month')
        return
      }
    }

    try {
      await config.updateFn(submitTargetId, { status: 'SUBMITTED' })
      toast.success('Report submitted to Office')
      setSubmitTargetId(null)
      loadRecords()
    } catch {
      toast.error('Failed to submit report')
    }
  }

  const parseExtraEntries = (
    json?: string | null
  ): {
    sections: Record<string, Record<string, any>[]>
    makeReplicas: Record<string, any[]>
  } => {
    const empty = { sections: {}, makeReplicas: {} }
    if (!json) return empty
    try {
      const parsed = JSON.parse(json)
      // Legacy: plain array -> assign to first configured section
      if (Array.isArray(parsed)) {
        const firstId = config.repeatableSections?.[0]?.sectionId
        return {
          sections: firstId ? { [firstId]: parsed } : {},
          makeReplicas: {},
        }
      }
      if (parsed && typeof parsed === 'object') {
        const sections: Record<string, Record<string, any>[]> = {}
        const makeReplicas: Record<string, any[]> =
          parsed.makeReplicas && typeof parsed.makeReplicas === 'object'
            ? parsed.makeReplicas
            : {}
        Object.keys(parsed).forEach((k) => {
          if (k === 'makeReplicas') return
          if (Array.isArray(parsed[k])) sections[k] = parsed[k]
        })
        return { sections, makeReplicas }
      }
      return empty
    } catch {
      return empty
    }
  }

  const getCarryForwardFieldKeys = () => {
    if (modalConfig.carryForwardFields !== undefined) {
      return new Set(modalConfig.carryForwardFields)
    }

    const carryForwardKeys = new Set<string>()
    modalConfig.formFields.forEach((field) => {
      const label = String(field.label || '')
      const isMakeStyleField = /make/i.test(label) && !/(load|consumption|analysis|inspection|chemical|water)/i.test(label)
      if (field.replicatable && field.replicateButtonLabel && isMakeStyleField) {
        carryForwardKeys.add(field.key)
      }
    })
    return carryForwardKeys
  }

  const getCarryForwardReplicaLabelKeys = () => new Set(modalConfig.carryForwardReplicaLabels ?? [])

  const getReplicaDefaultLabel = (fieldKey: string, idx: number) => {
    if (fieldKey === 'aeMakeTypeKw') return getAuxEngineReplicaLabel(idx)
    if ((BOILER_LABELED_REPLICA_FIELDS as readonly string[]).includes(fieldKey)) return getBoilerReplicaLabel(fieldKey, idx)
    if (fieldKey === 'igIgsMakeTypeCapacity') return getIgsReplicaLabel(idx)
    if (fieldKey === 'bwtsMakeTypeCapacity') return getBwtsReplicaLabel(idx)
    if (fieldKey === 'mac1MakeModelCapacity') return getCompressorReplicaLabel(idx)
    if (fieldKey === 'hfoPurifier1MakeModel') return getPurifierReplicaLabel(idx)
    if (fieldKey === 'fwgMakeTypeCapacity' || fieldKey === 'dateOfLastMaintenance') return getFwgReplicaLabel(fieldKey, idx)
    return `Field ${idx + 2}`
  }

  const buildCarryForwardReplicaMap = (source: Record<string, any[]>) => {
    const labelOnlyKeys = getCarryForwardReplicaLabelKeys()
    const nextMap: Record<string, any[]> = {}

    // For each make/identifier field, carry forward the label (machine name) only.
    // Then for every linked field in the same group, generate matching blank entries
    // so the form shows the correct number of replica rows with empty data.
    labelOnlyKeys.forEach((makeFieldKey) => {
      const current = Array.isArray(source[makeFieldKey]) ? source[makeFieldKey] : []
      if (current.length === 0) return

      // Carry forward the make field with label preserved, value cleared
      nextMap[makeFieldKey] = current.map((entry, idx) => {
        const label =
          entry && typeof entry === 'object' && !Array.isArray(entry) && 'label' in entry
            ? String((entry as any).label ?? '').trim()
            : ''
        return {
          label: label || getReplicaDefaultLabel(makeFieldKey, idx),
          value: '',  // clear the data value for the new month
        }
      })

      // Determine linked fields for this make field and generate blank replica entries
      const linkedFields: readonly string[] =
        makeFieldKey === 'aeMakeTypeKw' ? AUX_ENGINE_LINKED_REPLICA_FIELDS :
          makeFieldKey === 'igIgsMakeTypeCapacity' ? IGS_LINKED_REPLICA_FIELDS :
            makeFieldKey === 'bwtsMakeTypeCapacity' ? BWTS_LINKED_REPLICA_FIELDS :
              makeFieldKey === 'fwgMakeTypeCapacity' ? FWG_LINKED_REPLICA_FIELDS :
                makeFieldKey === 'boilerMakeTypeCapacity' ? ['boilerMakeTypeCapacity', ...BOILER_LINKED_REPLICA_FIELDS] :
                  makeFieldKey === 'mac1MakeModelCapacity' ? COMPRESSOR_LINKED_REPLICA_FIELDS :
                    makeFieldKey === 'hfoPurifier1MakeModel' ? PURIFIER_LINKED_REPLICA_FIELDS :
                      []

      linkedFields.forEach((linkedKey) => {
        if (linkedKey === makeFieldKey) return  // already handled above
        const isLabeled = (LABELED_REPLICA_FIELDS as readonly string[]).includes(linkedKey)
        nextMap[linkedKey] = current.map((_, idx) => {
          if (isLabeled) {
            return { label: getReplicaDefaultLabel(makeFieldKey, idx), value: '' }
          }
          return { label: '', value: '' }
        })
      })
    })

    return nextMap
  }

  const parseExtraEntriesForReport = (
    registryItem: MachineryReportRegistryItem,
    json?: string | null
  ): {
    sections: Record<string, Record<string, any>[]>
    makeReplicas: Record<string, any[]>
  } => {
    const empty = { sections: {}, makeReplicas: {} }
    if (!json) return empty
    try {
      const parsed = JSON.parse(json)
      if (Array.isArray(parsed)) {
        const firstId = registryItem.repeatableSections?.[0]?.sectionId
        return {
          sections: firstId ? { [firstId]: parsed } : {},
          makeReplicas: {},
        }
      }
      if (parsed && typeof parsed === 'object') {
        const sections: Record<string, Record<string, any>[]> = {}
        const makeReplicas: Record<string, any[]> =
          parsed.makeReplicas && typeof parsed.makeReplicas === 'object'
            ? parsed.makeReplicas
            : {}
        Object.keys(parsed).forEach((k) => {
          if (k === 'makeReplicas') return
          if (Array.isArray(parsed[k])) sections[k] = parsed[k]
        })
        return { sections, makeReplicas }
      }
      return empty
    } catch {
      return empty
    }
  }

  const buildCarryForwardReplicaMapForReport = (
    item: { carryForwardReplicaLabels?: string[] },
    source: Record<string, any[]>
  ) => {
    const labelOnlyKeys = new Set<string>(item.carryForwardReplicaLabels ?? [])
    const nextMap: Record<string, any[]> = {}

    labelOnlyKeys.forEach((makeFieldKey: string) => {
      const current = Array.isArray(source[makeFieldKey]) ? source[makeFieldKey] : []
      if (current.length === 0) return

      nextMap[makeFieldKey] = current.map((entry: any, idx: number) => {
        const label =
          entry && typeof entry === 'object' && !Array.isArray(entry) && 'label' in entry
            ? String((entry as any).label ?? '').trim()
            : ''
        return {
          label: label || getReplicaDefaultLabel(makeFieldKey, idx),
          value: '',
        }
      })

      const linkedFields: readonly string[] =
        makeFieldKey === 'aeMakeTypeKw' ? AUX_ENGINE_LINKED_REPLICA_FIELDS :
          makeFieldKey === 'igIgsMakeTypeCapacity' ? IGS_LINKED_REPLICA_FIELDS :
            makeFieldKey === 'bwtsMakeTypeCapacity' ? BWTS_LINKED_REPLICA_FIELDS :
              makeFieldKey === 'fwgMakeTypeCapacity' ? FWG_LINKED_REPLICA_FIELDS :
                makeFieldKey === 'boilerMakeTypeCapacity' ? ['boilerMakeTypeCapacity', ...BOILER_LINKED_REPLICA_FIELDS] :
                  makeFieldKey === 'mac1MakeModelCapacity' ? COMPRESSOR_LINKED_REPLICA_FIELDS :
                    makeFieldKey === 'hfoPurifier1MakeModel' ? PURIFIER_LINKED_REPLICA_FIELDS :
                      []

      linkedFields.forEach((linkedKey: string) => {
        if (linkedKey === makeFieldKey) return
        const isLabeled = (LABELED_REPLICA_FIELDS as readonly string[]).includes(linkedKey)
        nextMap[linkedKey] = current.map((_: any, idx: number) => {
          if (isLabeled) {
            return { label: getReplicaDefaultLabel(makeFieldKey, idx), value: '' }
          }
          return { label: '', value: '' }
        })
      })
    })

    return nextMap
  }

  const applyVesselDefaults = async (vid: number, preferredMonthParts: any, currentForm: any) => {
    const f = { ...currentForm }
    const carryForwardKeys = getCarryForwardFieldKeys()
    let hasAppliedDefaults = false
    if (vid && modalConfig.defaultsFn) {
      try {
        const defaults = await modalConfig.defaultsFn(vid)
        if (defaults && typeof defaults === 'object') {
          carryForwardKeys.forEach((k) => {
            const v = (defaults as any)[k]
            if (v !== undefined && v !== null && v !== '') {
              f[k] = v
            }
          })
          const defaultsExtraEntries = parseExtraEntries((defaults as any).extraEntriesJson)
          f._makeReplicas = {
            ...buildCarryForwardReplicaMap(defaultsExtraEntries.makeReplicas),
          }
          hasAppliedDefaults = true
          f.id = null
          f.reportMonth = preferredMonthParts?.reportMonth ?? new Date().getMonth() + 1
          f.reportYear = preferredMonthParts?.reportYear ?? currentYear
          f.status = null
          f.officeRemarks = null
          f.vesselId = vid
        }
      } catch (err) {
        console.warn('Failed to load report defaults', err)
      }
    }

    if (vid && !hasAppliedDefaults) {
      const latestForVessel = records
        .filter((r) => Number(r?.vesselId) === Number(vid))
        .sort((a, b) => {
          const aYear = Number(a?.reportYear ?? 0)
          const bYear = Number(b?.reportYear ?? 0)
          if (aYear !== bYear) return bYear - aYear
          const aMonth = Number(a?.reportMonth ?? 0)
          const bMonth = Number(b?.reportMonth ?? 0)
          if (aMonth !== bMonth) return bMonth - aMonth
          return Number(b?.id ?? 0) - Number(a?.id ?? 0)
        })[0]

      if (latestForVessel && typeof latestForVessel === 'object') {
        carryForwardKeys.forEach((k) => {
          const v = (latestForVessel as any)[k]
          if (v !== undefined && v !== null && v !== '') {
            f[k] = v
          }
        })
        const latestExtraEntries = parseExtraEntries((latestForVessel as any).extraEntriesJson)
        f._makeReplicas = {
          ...buildCarryForwardReplicaMap(latestExtraEntries.makeReplicas),
        }
        f.id = null
        f.reportMonth = preferredMonthParts?.reportMonth ?? new Date().getMonth() + 1
        f.reportYear = preferredMonthParts?.reportYear ?? currentYear
        f.status = null
        f.officeRemarks = null
        f.vesselId = vid
      }
    }

    if (!f._makeReplicas) f._makeReplicas = {}
    f._extraEntries = {}
    return f
  }

  const initializeWizardData = async (vid: number, m: number, y: number): Promise<Record<string, any> | null> => {
    setWizardLoading(true)
    const newWizardData: Record<string, any> = {}
    try {
      await Promise.all(
        reportRegistry.map(async (registryItem) => {
          let existingRecord: any = null
          try {
            const existing = await registryItem.searchFn({
              vesselId: vid,
              month: m,
              year: y,
              activeOnly: true,
            })
            if (existing && existing.length > 0) {
              existingRecord = existing[0]
            }
          } catch (err) {
            console.error(`Failed searching existing report for ${registryItem.key}`, err)
          }

          if (existingRecord) {
            const parsed = parseExtraEntriesForReport(registryItem, existingRecord.extraEntriesJson)
            newWizardData[registryItem.key] = {
              ...existingRecord,
              _reportKey: registryItem.key,
              _extraEntries: parsed.sections,
              _makeReplicas: parsed.makeReplicas,
            }
          } else {
            const emptyFormFn = registryItem.emptyForm || (() => ({} as Record<string, any>))
            let f: Record<string, any> & {
              vesselId?: number | null
              reportMonth?: number | null
              reportYear?: number | null
              _makeReplicas?: Record<string, any[]>
              _extraEntries?: Record<string, Record<string, any>[]>
              _reportKey?: string
            } = emptyFormFn()
            f.vesselId = vid
            f.reportMonth = m
            f.reportYear = y

            let hasAppliedDefaults = false
            if (registryItem.defaultsFn) {
              try {
                const defaults = await registryItem.defaultsFn(vid)
                if (defaults && typeof defaults === 'object') {
                  const carryForwardKeys = new Set(registryItem.carryForwardFields ?? [])
                  carryForwardKeys.forEach((k) => {
                    const v = (defaults as any)[k]
                    if (v !== undefined && v !== null && v !== '') {
                      f[k] = v
                    }
                  })
                  const defaultsExtraEntries = parseExtraEntriesForReport(registryItem, (defaults as any).extraEntriesJson)
                  f._makeReplicas = buildCarryForwardReplicaMapForReport(
                    registryItem,
                    defaultsExtraEntries.makeReplicas
                  )
                  hasAppliedDefaults = true
                }
              } catch (err) {
                console.warn(`Failed loading defaults for ${registryItem.key}`, err)
              }
            }

            if (!hasAppliedDefaults) {
              try {
                let prevM = m - 1
                let prevY = y
                if (prevM === 0) {
                  prevM = 12
                  prevY--
                }
                const prevRes = await registryItem.searchFn({
                  vesselId: vid,
                  month: prevM,
                  year: prevY,
                  activeOnly: true,
                })
                const latestPrevious = prevRes && prevRes.length > 0 ? prevRes[0] : null

                if (latestPrevious) {
                  const carryForwardKeys = new Set(registryItem.carryForwardFields ?? [])
                  carryForwardKeys.forEach((k) => {
                    const v = (latestPrevious as any)[k]
                    if (v !== undefined && v !== null && v !== '') {
                      f[k] = v
                    }
                  })
                  const prevExtra = parseExtraEntriesForReport(registryItem, latestPrevious.extraEntriesJson)
                  f._makeReplicas = buildCarryForwardReplicaMapForReport(
                    registryItem,
                    prevExtra.makeReplicas
                  )
                }
              } catch (err) {
                console.warn(`Failed carry-forward search for ${registryItem.key}`, err)
              }
            }

            if (!f._makeReplicas) f._makeReplicas = {}
            f._extraEntries = {}
            f._reportKey = registryItem.key
            newWizardData[registryItem.key] = f
          }
        })
      )

      setWizardData(newWizardData)
      const currentReportKey = modalConfig.reportKey
      if (newWizardData[currentReportKey]) {
        setForm(newWizardData[currentReportKey])
      }
      return newWizardData
    } catch (err) {
      console.error("Failed to initialize wizard data", err)
      return null
    } finally {
      setWizardLoading(false)
    }
  }

  const handleOpenAdd = async (seed?: { vesselId?: number | null; reportYear?: number | null; reportMonth?: number | null }) => {
    setModalMode('add')
    setWizardLoading(true)

    const initialCompanyGroup = isGlobalScope ? filterCompanyGroup : ''
    const initialCompanyAdmin = isGlobalScope ? filterCompanyAdmin : ''
    setModalCompanyGroup(initialCompanyGroup)
    setModalCompanyAdmin(initialCompanyAdmin)

    const addVessels = isGlobalScope
      ? getModalFilteredVessels(initialCompanyGroup, initialCompanyAdmin)
      : vessels

    const crewVesselId = isShipboardUser ? Number(currentUser?.vessel?.id ?? 0) || null : null
    const preferredVesselId = seed?.vesselId ?? crewVesselId ?? (filterVessel ? Number(filterVessel) : null)
    const vesselId =
      preferredVesselId && addVessels.some((v: Vessel) => Number(v.id) === preferredVesselId)
        ? preferredVesselId
        : addVessels[0]?.id
          ? Number(addVessels[0].id)
          : null

    const preferredMonthKey = await resolvePreferredMonthKey(vesselId, seed)
    const preferredMonthParts = monthKeyToParts(preferredMonthKey)
    const m = preferredMonthParts?.reportMonth ?? new Date().getMonth() + 1
    const y = preferredMonthParts?.reportYear ?? currentYear

    if (vesselId) {
      await initializeWizardData(vesselId, m, y)
    } else {
      setWizardLoading(false)
    }
  }

  const handleOpenEdit = (r: any) => {
    if (r?.__isContinuationPlaceholder) {
      onRequestModalReportKey?.(config.reportKey)
      void handleOpenAdd({
        vesselId: r.vesselId != null ? Number(r.vesselId) : null,
        reportYear: r.reportYear != null ? Number(r.reportYear) : null,
        reportMonth: r.reportMonth != null ? Number(r.reportMonth) : null,
      })
      return
    }
    const parsed = parseExtraEntries(r.extraEntriesJson)
    setForm({ ...r, _extraEntries: parsed.sections, _makeReplicas: parsed.makeReplicas })
    setEditId(Number(r.id))
    setModalMode('edit')
  }

  const handleOpenView = (r: any) => {
    const parsed = parseExtraEntries(r.extraEntriesJson)
    setForm({ ...r, _extraEntries: parsed.sections, _makeReplicas: parsed.makeReplicas })
    setModalMode('view')
  }

  const confirmDelete = async () => {
    if (!deletingRecord?.id) return
    try {
      await config.deleteFn(Number(deletingRecord.id))
      toast.success('Report deleted', { position: 'top-center' })
      setDeletingRecord(null)
      loadRecords()
    } catch {
      toast.error('Failed to delete', { position: 'top-center' })
    }
  }

  const saveAllWizardReports = async (vid: number, m: number, y: number) => {
    setWizardLoading(true)
    try {
      const finalWizardData = {
        ...wizardData,
        [modalConfig.reportKey]: form,
      }

      // Guard: if ALL 7 reports already exist for this vessel/month,
      // auto-advance to the next month so the user can create fresh reports.
      const allReportsExist = reportRegistry.every((r) => {
        const report = finalWizardData[r.key]
        return report?.id != null && typeof report.id === 'number'
      })

      if (allReportsExist) {
        let nextM = m + 1
        let nextY = y
        if (nextM > 12) {
          nextM = 1
          nextY++
        }
        toast.info(
          `Reports for ${MONTHS_SHORT[m - 1]} ${y} already exist. Opening ${MONTHS_SHORT[nextM - 1]} ${nextY} instead.`,
          { position: 'top-center', autoClose: 3000 }
        )
        await initializeWizardData(vid, nextM, nextY)
        return
      }

      await Promise.all(
        reportRegistry.map(async (registryItem) => {
          const reportForm = finalWizardData[registryItem.key]
          if (!reportForm) return

          const { _extraEntries, _makeReplicas, _reportKey, ...rest } = reportForm
          const payload: Record<string, any> = { ...rest, vesselId: vid, reportMonth: m, reportYear: y }

          const isPlaceholder = typeof payload.id === 'string' && payload.id.includes('placeholder')
          const databaseId = !isPlaceholder && payload.id ? Number(payload.id) : null
          if (isPlaceholder || !payload.id) {
            delete payload.id
          }

          const combined: Record<string, any> = {}

          if (registryItem.repeatableSections && registryItem.repeatableSections.length > 0) {
            const source =
              _extraEntries && typeof _extraEntries === 'object' && !Array.isArray(_extraEntries)
                ? (_extraEntries as Record<string, any[]>)
                : {}
            registryItem.repeatableSections.forEach((s) => {
              const arr = Array.isArray(source[s.sectionId]) ? source[s.sectionId] : []
              if (arr.length > 0) combined[s.sectionId] = arr
            })
          }

          const replicaSource =
            _makeReplicas && typeof _makeReplicas === 'object' && !Array.isArray(_makeReplicas)
              ? (_makeReplicas as Record<string, any[]>)
              : {}
          const replicas: Record<string, any[]> = {}
          Object.keys(replicaSource).forEach((fieldKey) => {
            const arr = Array.isArray(replicaSource[fieldKey]) ? replicaSource[fieldKey] : []
            const cleaned = arr.filter((v) => {
              if (v && typeof v === 'object' && !Array.isArray(v) && 'value' in v) {
                const hasValue = (v as any).value !== undefined && (v as any).value !== null && (v as any).value !== ''
                const hasLabel = String((v as any).label ?? '').trim() !== ''
                if ((fieldKey === 'boilerMakeTypeCapacity' || (BOILER_LABELED_REPLICA_FIELDS as readonly string[]).includes(fieldKey)) && hasLabel) {
                  return true
                }
                return hasValue
              }
              return v !== undefined && v !== null && v !== ''
            })
            if (cleaned.length > 0) replicas[fieldKey] = cleaned
          })
          if (Object.keys(replicas).length > 0) combined.makeReplicas = replicas

          if (registryItem.repeatableSections?.length || Object.keys(replicas).length > 0) {
            payload.extraEntriesJson = Object.keys(combined).length > 0 ? JSON.stringify(combined) : null
          }

          if (databaseId) {
            if (registryItem.updateFn) {
              await registryItem.updateFn(databaseId, payload)
            }
          } else {
            if (registryItem.createFn) {
              await registryItem.createFn(payload)
            }
          }
        })
      )

      toast.success('All machinery reports saved successfully', { position: 'top-center' })
      setModalMode(null)
      setWizardData({})
      loadRecords()
    } catch (e: any) {
      toast.error(pickApiErrorMessage(e, 'Failed to save machinery reports'), { position: 'top-center' })
    } finally {
      setWizardLoading(false)
    }
  }

  const handleSave = async () => {
    const effectiveVesselId =
      form.vesselId || (isShipboardUser ? Number(currentUser?.vessel?.id ?? 0) || null : null)

    if (!effectiveVesselId) {
      toast.error('Please select a vessel', { position: 'top-center' })
      return
    }
    if (!form.reportMonth || !form.reportYear) {
      toast.error('Month and Year are required', { position: 'top-center' })
      return
    }

    if (modalMode === 'add') {
      await saveAllWizardReports(effectiveVesselId, form.reportMonth, form.reportYear)
      return
    }

    try {
      const { _extraEntries, _makeReplicas, ...rest } = form
      const payload: Record<string, any> = { ...rest, vesselId: effectiveVesselId }

      // Build combined JSON: { makeReplicas: {fieldKey: [...]}, <sectionId>: [...] }
      const combined: Record<string, any> = {}

      const effectiveConfig = config
      if (effectiveConfig.repeatableSections && effectiveConfig.repeatableSections.length > 0) {
        const source =
          _extraEntries && typeof _extraEntries === 'object' && !Array.isArray(_extraEntries)
            ? (_extraEntries as Record<string, any[]>)
            : {}
        effectiveConfig.repeatableSections.forEach((s) => {
          const arr = Array.isArray(source[s.sectionId]) ? source[s.sectionId] : []
          if (arr.length > 0) combined[s.sectionId] = arr
        })
      }

      const replicaSource =
        _makeReplicas && typeof _makeReplicas === 'object' && !Array.isArray(_makeReplicas)
          ? (_makeReplicas as Record<string, any[]>)
          : {}
      const replicas: Record<string, any[]> = {}
      Object.keys(replicaSource).forEach((fieldKey) => {
        const arr = Array.isArray(replicaSource[fieldKey]) ? replicaSource[fieldKey] : []
        const cleaned = arr.filter((v) => {
          if (v && typeof v === 'object' && !Array.isArray(v) && 'value' in v) {
            const hasValue = (v as any).value !== undefined && (v as any).value !== null && (v as any).value !== ''
            const hasLabel = String((v as any).label ?? '').trim() !== ''
            if ((fieldKey === 'boilerMakeTypeCapacity' || (BOILER_LABELED_REPLICA_FIELDS as readonly string[]).includes(fieldKey)) && hasLabel) {
              return true
            }
            return hasValue
          }
          return v !== undefined && v !== null && v !== ''
        })
        if (cleaned.length > 0) replicas[fieldKey] = cleaned
      })
      if (Object.keys(replicas).length > 0) combined.makeReplicas = replicas

      if (effectiveConfig.repeatableSections?.length || Object.keys(replicas).length > 0) {
        payload.extraEntriesJson = Object.keys(combined).length > 0 ? JSON.stringify(combined) : null
      }

      if (editId) {
        await config.updateFn(editId, payload)
        toast.success('Report updated', { position: 'top-center' })
      }
      setModalMode(null)
      loadRecords()
    } catch (e: any) {
      toast.error(pickApiErrorMessage(e, 'Failed to save'), { position: 'top-center' })
    }
  }

  const handleNext = () => {
    const activeIndex = addReportOptions.findIndex((r) => r.key === modalConfig.reportKey)
    if (activeIndex >= 0 && activeIndex < addReportOptions.length - 1) {
      const currentReportKey = modalConfig.reportKey
      setWizardData((prev) => ({
        ...prev,
        [currentReportKey]: form,
      }))
      const nextReport = addReportOptions[activeIndex + 1]
      onSwitchAddReport?.(nextReport.key, {
        vesselId: form.vesselId ?? null,
        reportYear: form.reportYear ?? null,
        reportMonth: form.reportMonth ?? null,
      })
    }
  }

  const handlePrevious = () => {
    const activeIndex = addReportOptions.findIndex((r) => r.key === modalConfig.reportKey)
    if (activeIndex > 0) {
      const currentReportKey = modalConfig.reportKey
      setWizardData((prev) => ({
        ...prev,
        [currentReportKey]: form,
      }))
      const prevReport = addReportOptions[activeIndex - 1]
      onSwitchAddReport?.(prevReport.key, {
        vesselId: form.vesselId ?? null,
        reportYear: form.reportYear ?? null,
        reportMonth: form.reportMonth ?? null,
      })
    }
  }

  const setField = (key: string, value: any) => setForm((prev) => ({ ...prev, [key]: value }))

  const getSectionConfig = (sectionId: string) =>
    (modalMode === 'add' ? modalConfig : config).repeatableSections?.find((s) => s.sectionId === sectionId)

  const addExtraEntry = (sectionId: string) => {
    const section = getSectionConfig(sectionId)
    if (!section) return
    const blank: Record<string, any> = {}
    section.fields.forEach((f) => {
      blank[f.key] = f.type === 'number' ? null : ''
    })
    setForm((prev) => {
      const map = (prev._extraEntries && typeof prev._extraEntries === 'object'
        ? prev._extraEntries
        : {}) as Record<string, Record<string, any>[]>
      const current = Array.isArray(map[sectionId]) ? map[sectionId] : []
      return { ...prev, _extraEntries: { ...map, [sectionId]: [...current, blank] } }
    })
  }

  const removeExtraEntry = (sectionId: string, idx: number) => {
    setForm((prev) => {
      const map = (prev._extraEntries && typeof prev._extraEntries === 'object'
        ? prev._extraEntries
        : {}) as Record<string, Record<string, any>[]>
      const current = Array.isArray(map[sectionId]) ? [...map[sectionId]] : []
      current.splice(idx, 1)
      return { ...prev, _extraEntries: { ...map, [sectionId]: current } }
    })
  }

  const setExtraEntryField = (sectionId: string, idx: number, key: string, value: any) => {
    setForm((prev) => {
      const map = (prev._extraEntries && typeof prev._extraEntries === 'object'
        ? prev._extraEntries
        : {}) as Record<string, Record<string, any>[]>
      const current = Array.isArray(map[sectionId]) ? [...map[sectionId]] : []
      current[idx] = { ...(current[idx] || {}), [key]: value }
      return { ...prev, _extraEntries: { ...map, [sectionId]: current } }
    })
  }

  const getReplicaArray = (formState: Record<string, any>, fieldKey: string): any[] => {
    const map =
      formState._makeReplicas && typeof formState._makeReplicas === 'object' && !Array.isArray(formState._makeReplicas)
        ? (formState._makeReplicas as Record<string, any[]>)
        : {}
    return Array.isArray(map[fieldKey]) ? map[fieldKey] : []
  }

  const addMakeReplica = (fieldKey: string, fieldType: FieldType) => {
    setForm((prev) => {
      const map = (prev._makeReplicas && typeof prev._makeReplicas === 'object' && !Array.isArray(prev._makeReplicas)
        ? prev._makeReplicas
        : {}) as Record<string, any[]>
      if (fieldKey === 'aeMakeTypeKw') {
        const nextMap = { ...map }
        AUX_ENGINE_LINKED_REPLICA_FIELDS.forEach((linkedKey) => {
          const current = Array.isArray(nextMap[linkedKey]) ? nextMap[linkedKey] : []
          nextMap[linkedKey] = [...current, { label: getAuxEngineReplicaLabel(current.length), value: '' }]
        })
        return { ...prev, _makeReplicas: nextMap }
      }
      if (fieldKey === 'igIgsMakeTypeCapacity') {
        const nextMap = { ...map }
        IGS_LINKED_REPLICA_FIELDS.forEach((linkedKey) => {
          const current = Array.isArray(nextMap[linkedKey]) ? nextMap[linkedKey] : []
          if (linkedKey === 'igIgsMakeTypeCapacity' || linkedKey === 'igIgsPerformanceTestDatePassed') {
            nextMap[linkedKey] = [...current, { label: getIgsReplicaLabel(current.length), value: '' }]
          } else {
            nextMap[linkedKey] = [...current, '']
          }
        })
        return { ...prev, _makeReplicas: nextMap }
      }
      if (fieldKey === 'bwtsMakeTypeCapacity') {
        const nextMap = { ...map }
        BWTS_LINKED_REPLICA_FIELDS.forEach((linkedKey) => {
          const current = Array.isArray(nextMap[linkedKey]) ? nextMap[linkedKey] : []
          nextMap[linkedKey] =
            linkedKey === 'bwtsMakeTypeCapacity'
              ? [...current, { label: getBwtsReplicaLabel(current.length), value: '' }]
              : [...current, '']
        })
        return { ...prev, _makeReplicas: nextMap }
      }
      if (fieldKey === 'fwgMakeTypeCapacity' || fieldKey === 'dateOfLastMaintenance') {
        const nextMap = { ...map }
        const current = Array.isArray(nextMap[fieldKey]) ? nextMap[fieldKey] : []
        nextMap[fieldKey] = [...current, { label: getFwgReplicaLabel(fieldKey, current.length), value: '' }]
        return { ...prev, _makeReplicas: nextMap }
      }
      if ((BOILER_LINKED_REPLICA_FIELDS as readonly string[]).includes(fieldKey)) {
        const nextMap = { ...map }
        BOILER_LINKED_REPLICA_FIELDS.forEach((linkedKey) => {
          const current = Array.isArray(nextMap[linkedKey]) ? nextMap[linkedKey] : []
          const isLabeled = (BOILER_LABELED_REPLICA_FIELDS as readonly string[]).includes(linkedKey)
          nextMap[linkedKey] = [
            ...current,
            isLabeled
              ? {
                label: getBoilerReplicaLabel(linkedKey, current.length),
                value: '',
              }
              : '',
          ]
        })
        return { ...prev, _makeReplicas: nextMap }
      }
      if (fieldKey === 'mac1MakeModelCapacity') {
        const nextMap = { ...map }
        COMPRESSOR_LINKED_REPLICA_FIELDS.forEach((linkedKey) => {
          const current = Array.isArray(nextMap[linkedKey]) ? nextMap[linkedKey] : []
          nextMap[linkedKey] =
            linkedKey === 'mac1MakeModelCapacity'
              ? [...current, { label: getCompressorReplicaLabel(current.length), value: '' }]
              : [...current, '']
        })
        return { ...prev, _makeReplicas: nextMap }
      }
      if (fieldKey === 'hfoPurifier1MakeModel') {
        const nextMap = { ...map }
        PURIFIER_LINKED_REPLICA_FIELDS.forEach((linkedKey) => {
          const current = Array.isArray(nextMap[linkedKey]) ? nextMap[linkedKey] : []
          nextMap[linkedKey] =
            linkedKey === 'hfoPurifier1MakeModel'
              ? [...current, { label: getPurifierReplicaLabel(current.length), value: '' }]
              : [...current, '']
        })
        return { ...prev, _makeReplicas: nextMap }
      }
      const current = Array.isArray(map[fieldKey]) ? map[fieldKey] : []
      const blank =
        LABELED_REPLICA_FIELDS.includes(fieldKey as (typeof LABELED_REPLICA_FIELDS)[number])
          ? { label: `Field ${current.length + 2}`, value: fieldType === 'number' ? null : '' }
          : fieldType === 'number'
            ? null
            : ''
      return { ...prev, _makeReplicas: { ...map, [fieldKey]: [...current, blank] } }
    })
  }

  const removeMakeReplica = (fieldKey: string, idx: number) => {
    setForm((prev) => {
      const map = (prev._makeReplicas && typeof prev._makeReplicas === 'object' && !Array.isArray(prev._makeReplicas)
        ? prev._makeReplicas
        : {}) as Record<string, any[]>
      if (fieldKey === 'aeMakeTypeKw') {
        const nextMap = { ...map }
        AUX_ENGINE_LINKED_REPLICA_FIELDS.forEach((linkedKey) => {
          const current = Array.isArray(nextMap[linkedKey]) ? [...nextMap[linkedKey]] : []
          current.splice(idx, 1)
          nextMap[linkedKey] = current
        })
        return { ...prev, _makeReplicas: nextMap }
      }
      if (fieldKey === 'igIgsMakeTypeCapacity') {
        const nextMap = { ...map }
        IGS_LINKED_REPLICA_FIELDS.forEach((linkedKey) => {
          const current = Array.isArray(nextMap[linkedKey]) ? [...nextMap[linkedKey]] : []
          current.splice(idx, 1)
          nextMap[linkedKey] = current
        })
        return { ...prev, _makeReplicas: nextMap }
      }
      if (fieldKey === 'bwtsMakeTypeCapacity') {
        const nextMap = { ...map }
        BWTS_LINKED_REPLICA_FIELDS.forEach((linkedKey) => {
          const current = Array.isArray(nextMap[linkedKey]) ? [...nextMap[linkedKey]] : []
          current.splice(idx, 1)
          nextMap[linkedKey] = current
        })
        return { ...prev, _makeReplicas: nextMap }
      }
      if (fieldKey === 'fwgMakeTypeCapacity') {
        const nextMap = { ...map }
        FWG_LINKED_REPLICA_FIELDS.forEach((linkedKey) => {
          const current = Array.isArray(nextMap[linkedKey]) ? [...nextMap[linkedKey]] : []
          current.splice(idx, 1)
          nextMap[linkedKey] = current
        })
        return { ...prev, _makeReplicas: nextMap }
      }
      if ((BOILER_LINKED_REPLICA_FIELDS as readonly string[]).includes(fieldKey)) {
        const nextMap = { ...map }
        BOILER_LINKED_REPLICA_FIELDS.forEach((linkedKey) => {
          const current = Array.isArray(nextMap[linkedKey]) ? [...nextMap[linkedKey]] : []
          current.splice(idx, 1)
          nextMap[linkedKey] = current
        })
        return { ...prev, _makeReplicas: nextMap }
      }
      if (fieldKey === 'mac1MakeModelCapacity') {
        const nextMap = { ...map }
        COMPRESSOR_LINKED_REPLICA_FIELDS.forEach((linkedKey) => {
          const current = Array.isArray(nextMap[linkedKey]) ? [...nextMap[linkedKey]] : []
          current.splice(idx, 1)
          nextMap[linkedKey] = current
        })
        return { ...prev, _makeReplicas: nextMap }
      }
      if (fieldKey === 'hfoPurifier1MakeModel') {
        const nextMap = { ...map }
        PURIFIER_LINKED_REPLICA_FIELDS.forEach((linkedKey) => {
          const current = Array.isArray(nextMap[linkedKey]) ? [...nextMap[linkedKey]] : []
          current.splice(idx, 1)
          nextMap[linkedKey] = current
        })
        return { ...prev, _makeReplicas: nextMap }
      }
      const current = Array.isArray(map[fieldKey]) ? [...map[fieldKey]] : []
      current.splice(idx, 1)
      return { ...prev, _makeReplicas: { ...map, [fieldKey]: current } }
    })
  }

  const setMakeReplicaValue = (fieldKey: string, idx: number, value: any) => {
    setForm((prev) => {
      const map = (prev._makeReplicas && typeof prev._makeReplicas === 'object' && !Array.isArray(prev._makeReplicas)
        ? prev._makeReplicas
        : {}) as Record<string, any[]>
      const current = Array.isArray(map[fieldKey]) ? [...map[fieldKey]] : []
      const existing = current[idx]
      if (
        LABELED_REPLICA_FIELDS.includes(fieldKey as (typeof LABELED_REPLICA_FIELDS)[number]) &&
        existing &&
        typeof existing === 'object' &&
        !Array.isArray(existing)
      ) {
        current[idx] = { ...existing, value }
      } else if (
        LABELED_REPLICA_FIELDS.includes(fieldKey as (typeof LABELED_REPLICA_FIELDS)[number])
      ) {
        current[idx] = {
          label: isAuxEngineReplicaField(fieldKey)
            ? getAuxEngineReplicaLabel(idx)
            : isIgsReplicaField(fieldKey)
              ? getIgsReplicaLabel(idx)
              : (BOILER_LABELED_REPLICA_FIELDS as readonly string[]).includes(fieldKey)
                ? getBoilerReplicaLabel(fieldKey, idx)
                : fieldKey === 'mac1MakeModelCapacity'
                  ? getCompressorReplicaLabel(idx)
                  : fieldKey === 'hfoPurifier1MakeModel'
                    ? getPurifierReplicaLabel(idx)
                    : fieldKey === 'bwtsMakeTypeCapacity'
                      ? getBwtsReplicaLabel(idx)
                      : fieldKey === 'fwgMakeTypeCapacity' || fieldKey === 'dateOfLastMaintenance'
                        ? getFwgReplicaLabel(fieldKey, idx)
                        : `Field ${idx + 2}`,
          value,
        }
      } else {
        current[idx] = value
      }

      return { ...prev, _makeReplicas: { ...map, [fieldKey]: current } }
    })
  }

  const setMakeReplicaLabel = (fieldKey: string, idx: number, label: string) => {
    setForm((prev) => {
      const map = (prev._makeReplicas && typeof prev._makeReplicas === 'object' && !Array.isArray(prev._makeReplicas)
        ? prev._makeReplicas
        : {}) as Record<string, any[]>

      // Only update the label for the specific field being edited, not all linked fields
      const current = Array.isArray(map[fieldKey]) ? [...map[fieldKey]] : []
      const existing = current[idx]
      if (existing && typeof existing === 'object' && !Array.isArray(existing)) {
        current[idx] = { ...existing, label }
      } else {
        current[idx] = { label, value: existing ?? '' }
      }
      return { ...prev, _makeReplicas: { ...map, [fieldKey]: current } }
    })
  }

  const getReplicaFieldValue = (fieldKey: string, idx: number) => {
    const entry = getReplicaArray(form, fieldKey)[idx]
    return entry && typeof entry === 'object' && !Array.isArray(entry) && 'value' in entry
      ? (entry as any).value
      : entry
  }

  const renderMonthMultiSelectDropdown = (wrapperClassName: string) => (
    <div className={wrapperClassName}>
      <label className='form-label fw-bold text-dark'>Months Filter</label>
      <div className='position-relative'>
        <button
          type='button'
          className='form-select form-select-sm text-start'
          onClick={() => {
            setCompareMonthDropdownOpen((o) => !o)
            setVesselDropdownOpen(false)
          }}
        >
          {filterMonthKeys.length > 0 ? `${filterMonthKeys.length} month(s) selected` : 'Select months'}
        </button>
        {compareMonthDropdownOpen && (
          <div
            className='position-absolute bg-white border rounded shadow-sm w-100 mt-1 p-2'
            style={{ zIndex: 20, maxHeight: 280, overflowY: 'auto' }}
          >
            <div className='d-flex flex-column gap-3'>
              <div className='d-flex align-items-center justify-content-between bg-light rounded p-2 border'>
                <button
                  type='button'
                  className='btn btn-xs btn-icon btn-active-light-primary'
                  onClick={() => setCompareViewYear((prev) => prev - 1)}
                >
                  <i className='bi bi-chevron-left'></i>
                </button>
                <div className='d-flex align-items-center gap-2'>
                  <input
                    type='number'
                    className='form-control form-control-sm py-0 px-1 text-center fw-bolder border-0 bg-transparent'
                    style={{ width: '60px', fontSize: '15px' }}
                    value={compareViewYear}
                    onChange={(e) => setCompareViewYear(Number(e.target.value))}
                  />
                  <button
                    className='btn btn-xs btn-link p-0 fs-9 text-decoration-none'
                    onClick={() => {
                      const yearKeys = MONTHS_SHORT.map(
                        (_, idx) => `${compareViewYear}-${String(idx + 1).padStart(2, '0')}`
                      )
                      const allSelected = yearKeys.every((k) => filterMonthKeys.includes(k))
                      if (allSelected) {
                        handleMonthMultiSelectChange(filterMonthKeys.filter((k) => !yearKeys.includes(k)))
                      } else {
                        handleMonthMultiSelectChange(Array.from(new Set([...filterMonthKeys, ...yearKeys])))
                      }
                    }}
                  >
                    Toggle All
                  </button>
                </div>
                <button
                  type='button'
                  className='btn btn-xs btn-icon btn-active-light-primary'
                  onClick={() => setCompareViewYear((prev) => prev + 1)}
                >
                  <i className='bi bi-chevron-right'></i>
                </button>
              </div>
              <div className='d-grid' style={{ gridTemplateColumns: 'repeat(3, 1fr)', gap: '6px' }}>
                {MONTHS_SHORT.map((mLabel, idx) => {
                  const mk = `${compareViewYear}-${String(idx + 1).padStart(2, '0')}`
                  const selected = filterMonthKeys.includes(mk)
                  return (
                    <button
                      key={mk}
                      type='button'
                      className={`btn btn-sm py-2 px-0 border transition-all d-flex flex-column align-items-center justify-content-center ${selected
                        ? 'btn-primary border-primary shadow-sm'
                        : 'btn-light border-gray-200 text-muted hover:border-primary'
                        }`}
                      style={{ fontSize: '11px' }}
                      onClick={() => {
                        const next = selected
                          ? filterMonthKeys.filter((k) => k !== mk)
                          : [...filterMonthKeys, mk]
                        handleMonthMultiSelectChange(next)
                      }}
                    >
                      <span className='fw-bold'>{mLabel}</span>
                    </button>
                  )
                })}
              </div>
              {filterMonthKeys.length > 0 && (
                <div className='mt-2 border-top pt-3'>
                  <div className='d-flex justify-content-between align-items-center mb-2'>
                    <label className='fs-9 fw-bold text-muted text-uppercase'>Selected Periods</label>
                    <button
                      className='btn btn-xs btn-link p-0 fs-9'
                      onClick={() => handleMonthMultiSelectChange([])}
                    >
                      Clear All
                    </button>
                  </div>
                  <div className='d-flex flex-wrap gap-1' style={{ maxHeight: '100px', overflowY: 'auto' }}>
                    {Array.from(new Set(filterMonthKeys.map((k) => k.split('-')[0])))
                      .sort((a, b) => (a < b ? 1 : -1))
                      .map((year) => {
                        const yearMonths = filterMonthKeys
                          .filter((k) => k.startsWith(year + '-'))
                          .map((k) => MONTHS_SHORT[Number(k.split('-')[1]) - 1])
                          .join(', ')
                        return (
                          <div
                            key={year}
                            className='badge badge-light-primary d-flex align-items-center gap-1 py-1 px-2 border border-primary border-opacity-10'
                          >
                            <span className='fw-bold'>{year}:</span>
                            <span
                              className='text-dark fs-9'
                              style={{ maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                              title={yearMonths}
                            >
                              {yearMonths}
                            </span>
                            <i
                              className='bi bi-x fs-7 cursor-pointer hover:text-danger'
                              onClick={(e) => {
                                e.stopPropagation()
                                handleMonthMultiSelectChange(
                                  filterMonthKeys.filter((k) => !k.startsWith(year + '-'))
                                )
                              }}
                            ></i>
                          </div>
                        )
                      })}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )

  const mergedRecords = useMemo(() => {
    return [...records, ...continuationRows].sort((a, b) => {
      const aYear = Number(a?.reportYear ?? 0)
      const bYear = Number(b?.reportYear ?? 0)
      if (aYear !== bYear) return bYear - aYear
      const aMonth = Number(a?.reportMonth ?? 0)
      const bMonth = Number(b?.reportMonth ?? 0)
      if (aMonth !== bMonth) return bMonth - aMonth
      return Number(b?.vesselId ?? 0) - Number(a?.vesselId ?? 0)
    })
  }, [records, continuationRows])

  const filteredRecords = useMemo(() => {
    return mergedRecords.filter((r) => {
      const statusVisibleToUser =
        !isRestrictedOfficeUser || r.status === 'SUBMITTED' || r.status === 'REVIEWED'

      const vesselMatch = useMultiSelectFilters
        ? filterVesselIds.length === 0 ||
        (r.vesselId != null && filterVesselIds.includes(Number(r.vesselId)))
        : !filterVessel || Number(r.vesselId) === Number(filterVessel)

      const key = recordMonthKey(r)
      const explicitMonthMatch = useMultiMonthFilters
        ? filterMonthKeys.length === 0 || (key !== '' && filterMonthKeys.includes(key))
        : true

      const singleMonthMatch =
        useMultiMonthFilters || !filterMonthValue || (key !== '' && key === filterMonthValue)

      return statusVisibleToUser && vesselMatch && explicitMonthMatch && singleMonthMatch
    })
  }, [mergedRecords, isRestrictedOfficeUser, useMultiSelectFilters, useMultiMonthFilters, filterVesselIds, filterVessel, filterMonthKeys, filterMonthValue])

  const totalPages = Math.ceil(filteredRecords.length / rowsPerPage)
  const pageRecords = filteredRecords.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage)
  const isReadOnly = modalMode === 'view'
  const vesselOptions = isGlobalScope && modalMode === 'add' ? modalVessels : vessels
  const showVesselColumn = isOfficeUser
  const availableMonthKeys = useMemo(() => {
    const years = records
      .map((r) => Number(r.reportYear))
      .filter((y) => Number.isFinite(y) && y > 0)
    const minYear = years.length ? Math.min(...years, currentYear - 1) : currentYear - 1
    const maxYear = years.length ? Math.max(...years, currentYear + 1) : currentYear + 1

    const keys: string[] = []
    for (let y = minYear; y <= maxYear; y++) {
      for (let m = 1; m <= 12; m++) {
        keys.push(`${y}-${String(m).padStart(2, '0')}`)
      }
    }

    return keys.sort((a, b) => (a < b ? 1 : -1))
  }, [records])

  const VIEW_LABEL = 'fw-semibold text-muted d-block mb-1 fs-8'
  const VIEW_VALUE = 'fw-bold text-dark fs-7'
  const VIEW_CHIP_STYLE: React.CSSProperties = {
    display: 'inline-block',
    backgroundColor: '#f1f3f5',
    border: '1px solid #dee2e6',
    borderRadius: '6px',
    padding: '2px 10px',
    minWidth: '48px',
    alignSelf: 'flex-start',
  }

  /**
   * Renders a field label as a colored numbered badge (view mode) or plain label (edit mode).
   * badgeIndex: 0-based index of the replica (0 = first added unit = badge #2, etc.)
   * unitNumber: the number shown inside the badge (e.g. 2 for the second unit)
   */
  const renderReplicaFieldLabel = (
    label: string,
    badgeColorIndex: number,
    unitNumber: number,
    editModeNode?: React.ReactNode
  ) => {
    const b = getBadgeStyle(badgeColorIndex)
    if (isReadOnly) {
      return (
        <div
          className='d-inline-flex align-items-center gap-2 mb-2 px-2 py-1 rounded-2'
          style={{ backgroundColor: b.backgroundColor, border: b.border }}
        >
          <span
            className='badge badge-circle fw-bold d-flex align-items-center justify-content-center flex-shrink-0'
            style={{ width: '20px', height: '20px', fontSize: '10px', backgroundColor: '#fff', color: b.color, border: b.border }}
          >
            {unitNumber}
          </span>
          <span className='fw-semibold fs-8' style={{ color: b.color }}>{label}</span>
        </div>
      )
    }
    return editModeNode ?? <label className='form-label fw-semibold fs-7 text-dark mb-1'>{label}</label>
  }

  const renderViewField = (label: string, value: any, colSpan: number = 4) => {
    const isEmpty = value === undefined || value === null || value === ''
    return (
      <div className={`col-md-${colSpan} mb-4`}>
        <label className={VIEW_LABEL}>{label}</label>
        <div className={VIEW_VALUE} style={isEmpty ? undefined : VIEW_CHIP_STYLE}>
          {value ?? '-'}
        </div>
      </div>
    )
  }

  const replicatableFieldMap = useMemo(() => {
    const map = new Map<string, FormFieldDef>()
    config.formFields.forEach((f) => {
      if (f.replicatable) map.set(f.key, f)
    })
    return map
  }, [config.formFields])
  const formFieldMap = useMemo(() => {
    const map = new Map<string, FormFieldDef>()
    config.formFields.forEach((f) => {
      map.set(f.key, f)
    })
    return map
  }, [config.formFields])

  const getRowReplicas = (row: any, fieldKey: string): any[] => {
    if (!row?.extraEntriesJson) return []
    try {
      const parsed = JSON.parse(row.extraEntriesJson)
      const replicas = parsed?.makeReplicas?.[fieldKey]
      return Array.isArray(replicas) ? replicas : []
    } catch {
      return []
    }
  }

  const getBadgeStyle = (index: number) => {
    const colors = [
      { bg: '#f1faff', color: '#009ef7', border: '#009ef7' }, // Blue
      { bg: '#e8fff3', color: '#50cd89', border: '#50cd89' }, // Green
      { bg: '#f8f5ff', color: '#7239ea', border: '#7239ea' }, // Purple
      { bg: '#fff8dd', color: '#ffc700', border: '#ffc700' }, // Yellow
      { bg: '#ffeee8', color: '#f1416c', border: '#f1416c' }, // Red
      { bg: '#eff2f5', color: '#181c32', border: '#181c32' }  // Dark
    ]
    const c = colors[index % colors.length]
    return {
      backgroundColor: c.bg,
      color: c.color,
      border: `1px solid ${c.border}`
    }
  }

  const ITEM_CARD_STYLE: React.CSSProperties = {
    width: '100%',
    minHeight: '36px',
    backgroundColor: '#ffffff',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    padding: '4px 8px 4px 28px',
    display: 'flex',
    alignItems: 'center',
    boxSizing: 'border-box',
    position: 'relative',
    whiteSpace: 'nowrap'
  }

  const renderColumnCell = (col: ColumnDef, row: any) => {
    if (col.render) return col.render(row)
    const field = replicatableFieldMap.get(col.key)
    const base = row[col.key]
    if (!field) return base ?? '-'
    const allReplicas = getRowReplicas(row, col.key)
    const baseIsEmpty = base === undefined || base === null || base === ''
    // If the flat column is empty, fall back to makeReplicas[0].value (handles reports where
    // AE #1 make was stored only in the replica map instead of the flat column)
    const fallbackBase = baseIsEmpty
      ? (() => {
        const first = allReplicas[0]
        if (first && typeof first === 'object' && !Array.isArray(first) && 'value' in first) {
          return (first as any).value ?? null
        }
        return null
      })()
      : null
    const effectiveBase = baseIsEmpty ? fallbackBase : base
    // When fallback is used, skip allReplicas[0] to avoid duplicating AE #1
    const replicaStartIdx = baseIsEmpty && fallbackBase !== null && fallbackBase !== '' ? 1 : 0
    const replicas = allReplicas
      .slice(replicaStartIdx)
      .map((v) => {
        if (v && typeof v === 'object' && !Array.isArray(v) && 'value' in v) return (v as any).value
        return v
      })
      .filter((v) => v !== undefined && v !== null && v !== '')
    const values: any[] = []
    if (effectiveBase !== undefined && effectiveBase !== null && effectiveBase !== '') values.push(effectiveBase)
    values.push(...replicas)
    if (values.length === 0) return '-'

    return (
      <div className='d-flex flex-column gap-2'>
        {values.map((v, i) => {
          const style = getBadgeStyle(i)
          return (
            <div key={i} style={{ ...ITEM_CARD_STYLE, backgroundColor: style.backgroundColor, border: style.border }}>
              <span
                className='position-absolute badge badge-circle fw-bold'
                style={{
                  top: '50%',
                  left: '4px',
                  transform: 'translateY(-50%)',
                  width: '18px',
                  height: '18px',
                  fontSize: '9px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: '#ffffff',
                  color: style.color,
                  border: style.border,
                }}
              >
                {i + 1}
              </span>
              <span className='fw-semibold fs-7 text-truncate' style={{ color: style.color }}>{String(v)}</span>
            </div>
          )
        })}
      </div>
    )
  }

  const sections = useMemo(() => {
    const map = new Map<string, FormFieldDef[]>()
    config.formFields.forEach((f) => {
      const sec = f.section || ''
      if (!map.has(sec)) map.set(sec, [])
      map.get(sec)!.push(f)
    })
    return map
  }, [config.formFields])

  // Modal-specific computed values (for independent modal report type)
  const modalFormFieldMap = useMemo(() => {
    const map = new Map<string, FormFieldDef>()
    modalConfig.formFields.forEach((f) => {
      map.set(f.key, f)
    })
    return map
  }, [modalConfig.formFields])

  const modalSections = useMemo(() => {
    const map = new Map<string, FormFieldDef[]>()
    modalConfig.formFields.forEach((f) => {
      const sec = f.section || ''
      if (!map.has(sec)) map.set(sec, [])
      map.get(sec)!.push(f)
    })
    return map
  }, [modalConfig.formFields])

  // Effective maps that switch based on modal mode (add uses modalConfig, edit/view uses config)
  const effectiveFormFieldMap = modalMode === 'add' ? modalFormFieldMap : formFieldMap
  const effectiveSections = modalMode === 'add' ? modalSections : sections

  const renderPrimitiveInput = (
    field: FormFieldDef,
    value: any,
    onChange: (v: any) => void
  ) => {
    if (isReadOnly) {
      let displayValue = value ?? '-'
      if (field.type === 'select' && field.options) {
        displayValue = field.options.find(o => o.value === value)?.label || displayValue
      }
      const isEmpty = displayValue === '-' || displayValue === '' || displayValue == null
      return (
        <div
          className={VIEW_VALUE}
          style={isEmpty ? undefined : VIEW_CHIP_STYLE}
        >
          {displayValue || '-'}
        </div>
      )
    }

    if (field.type === 'select') {
      return (
        <SearchableSelect
          className='form-select form-select-sm text-dark'
          style={{ backgroundColor: '#fff' }}
          value={value ?? ''}
          disabled={isReadOnly}
          onChange={(e) => onChange(e.target.value || null)}
        >
          <option value=''>Select...</option>
          {(field.options || []).map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </SearchableSelect>
      )
    }

    if (field.type === 'textarea') {
      return (
        <textarea
          className='form-control form-control-sm text-dark'
          style={{ backgroundColor: '#fff' }}
          rows={3}
          value={value ?? ''}
          disabled={isReadOnly}
          onChange={(e) => onChange(e.target.value)}
        />
      )
    }

    return (
      <input
        type={field.type}
        step={field.type === 'number' ? 'any' : undefined}
        className='form-control form-control-sm text-dark'
        style={{ backgroundColor: '#fff' }}
        value={value ?? ''}
        disabled={isReadOnly}
        onChange={(e) =>
          onChange(
            field.type === 'number'
              ? e.target.value
                ? Number(e.target.value)
                : null
              : e.target.value
          )
        }
      />
    )
  }

  const renderFormField = (field: FormFieldDef) => {
    const colSpan =
      field.key === 'quantityPluggedBoiler1Pipes'
        ? 12
        : field.key === 'dateOfLastWaterNodesCheck'
          ? 12
          : field.colSpan || 6
    const colClass = `col-md-${colSpan} mb-4`
    const val = form[field.key] ?? ''

    if (!field.replicatable) {
      return (
        <div className={colClass} key={field.key}>
          {isReadOnly && field.key === 'boiler1PerformanceTestDatePressure' ? (
            <>
              <div className='d-inline-flex align-items-center gap-2 mb-2 px-2 py-1 rounded-2' style={{ backgroundColor: '#f1faff', border: '1px solid #009ef7' }}>
                <span className='badge badge-circle fw-bold d-flex align-items-center justify-content-center flex-shrink-0' style={{ width: '20px', height: '20px', fontSize: '10px', backgroundColor: '#fff', color: '#009ef7', border: '1px solid #009ef7' }}>1</span>
                <span className='fw-semibold fs-8' style={{ color: '#009ef7' }}>{field.label}</span>
              </div>
              <div>{renderPrimitiveInput(field, val, (v) => setField(field.key, v))}</div>
            </>
          ) : (
            <>
              <label className='form-label fw-semibold fs-7 text-dark' title={field.label}>{field.label}</label>
              {renderPrimitiveInput(field, val, (v) => setField(field.key, v))}
            </>
          )}
        </div>
      )
    }

    const replicas = getReplicaArray(form, field.key)
    const buttonLabel = field.replicateButtonLabel || 'Add'
    const showReplicateButton =
      !isReadOnly &&
      ![
        'ae1MaxLoad',
        'lastDateBoiler1InternalInspection',
        'quantityPluggedBoiler1Pipes',
        'igIgsPerformanceTestDatePassed',
        'dateOfLastSensorsCalibration',
        'lastTimeDemisterCleaned',
        'dateOfLastShoreServiceAttendance',
        'dateOfLastInternalInspection',
        'dateOfLastWaterNodesCheck',
        'bwtsOperationalTestDatePassed',
        'minStockCriticalSparePartsChecked',
        'dateOfLastMaintenance',
        'dateOfLastBoilerWaterAnalysis',
        'waterAnalysisOk',
        'enoughStockOfChemicalForAnalysis',
        'dailyConsumptionVlsfoMgo',
        'mac1GoodWorkingCondition',
        'mac1PmsSparePartAvailable',
        'hfoPurifier1GoodWorkingCondition',
        'hfoPurifier1PmsSparePartAvailable',
      ].includes(field.key)
    const showReplicaRemoveButton =
      !isReadOnly &&
      ![
        'ae1MaxLoad',
        'lastDateBoiler1InternalInspection',
        'quantityPluggedBoiler1Pipes',
        'igIgsPerformanceTestDatePassed',
        'dateOfLastSensorsCalibration',
        'lastTimeDemisterCleaned',
        'dateOfLastShoreServiceAttendance',
        'dateOfLastInternalInspection',
        'dateOfLastWaterNodesCheck',
        'bwtsOperationalTestDatePassed',
        'minStockCriticalSparePartsChecked',
        'dateOfLastMaintenance',
        'dateOfLastBoilerWaterAnalysis',
        'waterAnalysisOk',
        'enoughStockOfChemicalForAnalysis',
        'dailyConsumptionVlsfoMgo',
        'mac1GoodWorkingCondition',
        'mac1PmsSparePartAvailable',
        'hfoPurifier1GoodWorkingCondition',
        'hfoPurifier1PmsSparePartAvailable',
      ].includes(field.key)
    const isCompressorPrimaryField = field.key === 'mac1MakeModelCapacity'
    const isCompressorConditionField = field.key === 'mac1GoodWorkingCondition'
    const isCompressorPmsField = field.key === 'mac1PmsSparePartAvailable'
    const isPurifierPrimaryField = field.key === 'hfoPurifier1MakeModel'
    const isPurifierConditionField = field.key === 'hfoPurifier1GoodWorkingCondition'
    const isPurifierPmsField = field.key === 'hfoPurifier1PmsSparePartAvailable'
    const isBoilerPrimaryField = field.key === 'boilerMakeTypeCapacity'
    const isBoilerPerformanceField = field.key === 'boiler1PerformanceTestDatePressure'
    const isBoilerWaterDateField = field.key === 'dateOfLastBoilerWaterAnalysis'
    const isBoilerWaterStatusField = field.key === 'waterAnalysisOk'
    const isBoilerWaterChemicalField = field.key === 'enoughStockOfChemicalForAnalysis'
    const isBoilerInspectionField = field.key === 'lastDateBoiler1InternalInspection'
    const isBoilerPluggedPipesField = field.key === 'quantityPluggedBoiler1Pipes'
    const isIgsPrimaryField = field.key === 'igIgsMakeTypeCapacity'
    const isIgsPerformanceField = field.key === 'igIgsPerformanceTestDatePassed'
    const isIgsSensorsField = field.key === 'dateOfLastSensorsCalibration'
    const isIgsDemisterField = field.key === 'lastTimeDemisterCleaned'
    const isIgsShoreServiceField = field.key === 'dateOfLastShoreServiceAttendance'
    const isIgsInternalInspectionField = field.key === 'dateOfLastInternalInspection'
    const isIgsWaterNodesField = field.key === 'dateOfLastWaterNodesCheck'
    const isBwtsPrimaryField = field.key === 'bwtsMakeTypeCapacity'
    const isBwtsOperationalTestField = field.key === 'bwtsOperationalTestDatePassed'
    const isBwtsMinStockField = field.key === 'minStockCriticalSparePartsChecked'
    const isBwtsLastShoreServiceField = field.key === 'dateOfLastShoreServiceAttendance'
    const compressorConditionField = effectiveFormFieldMap.get('mac1GoodWorkingCondition')
    const compressorPmsField = effectiveFormFieldMap.get('mac1PmsSparePartAvailable')
    const purifierConditionField = effectiveFormFieldMap.get('hfoPurifier1GoodWorkingCondition')
    const purifierPmsField = effectiveFormFieldMap.get('hfoPurifier1PmsSparePartAvailable')
    const boilerPerformanceField = effectiveFormFieldMap.get('boiler1PerformanceTestDatePressure')
    const boilerWaterDateField = effectiveFormFieldMap.get('dateOfLastBoilerWaterAnalysis')
    const boilerWaterStatusField = effectiveFormFieldMap.get('waterAnalysisOk')
    const boilerWaterChemicalField = effectiveFormFieldMap.get('enoughStockOfChemicalForAnalysis')
    const boilerInspectionField = effectiveFormFieldMap.get('lastDateBoiler1InternalInspection')
    const boilerPluggedPipesField = effectiveFormFieldMap.get('quantityPluggedBoiler1Pipes')
    const igsMakeField = effectiveFormFieldMap.get('igIgsMakeTypeCapacity')
    const igsPerformanceField = effectiveFormFieldMap.get('igIgsPerformanceTestDatePassed')
    const igsSensorsField = effectiveFormFieldMap.get('dateOfLastSensorsCalibration')
    const igsDemisterField = effectiveFormFieldMap.get('lastTimeDemisterCleaned')
    const igsShoreServiceField = effectiveFormFieldMap.get('dateOfLastShoreServiceAttendance')
    const igsInternalInspectionField = effectiveFormFieldMap.get('dateOfLastInternalInspection')
    const igsWaterNodesField = effectiveFormFieldMap.get('dateOfLastWaterNodesCheck')

    // BWTS: keep the base fields together; render added BWTS bundles AFTER the base BWTS section.
    if (field.key === 'dateOfLastShoreServiceAttendance') {
      const bwtsMakeField = effectiveFormFieldMap.get('bwtsMakeTypeCapacity')
      const bwtsOperationalTestField = effectiveFormFieldMap.get('bwtsOperationalTestDatePassed')
      const bwtsMinStockField = effectiveFormFieldMap.get('minStockCriticalSparePartsChecked')
      const bwtsLastShoreServiceField = effectiveFormFieldMap.get('dateOfLastShoreServiceAttendance')
      const hasBwts = !!bwtsMakeField && !!bwtsOperationalTestField && !!bwtsMinStockField && !!bwtsLastShoreServiceField
      const bwtsReplicas = getReplicaArray(form, 'bwtsMakeTypeCapacity')

      if (hasBwts && bwtsReplicas.length > 0) {
        return (
          <>
            <div className={colClass} key={field.key}>
              <label className='form-label fw-semibold fs-7 text-dark'>{field.label}</label>
              {renderPrimitiveInput(field, val, (v) => setField(field.key, v))}
            </div>

            <div className='col-12 mb-4'>
              <div className='mt-1 d-flex flex-column gap-3'>
                {bwtsReplicas.map((rv, idx) => {
                  const bwtsLabel =
                    rv && typeof rv === 'object' && !Array.isArray(rv) && 'label' in rv
                      ? String((rv as any).label ?? '').trim()
                      : ''
                  const bwtsDisplayName = bwtsLabel || getBwtsReplicaLabel(idx)

                  return (
                    <div key={`bwtsMakeTypeCapacity-rep-${idx}`} className='mb-4 pt-2 border-top'>
                      <div className='d-flex justify-content-between align-items-center mb-3 gap-2'>
                        {isReadOnly ? (
                          (() => {
                            const bwtsBadge = getBadgeStyle(idx + 1); return (
                              <div className='d-inline-flex align-items-center gap-2 px-2 py-1 rounded-2' style={{ backgroundColor: bwtsBadge.backgroundColor, border: bwtsBadge.border }}>
                                <span className='badge badge-circle fw-bold d-flex align-items-center justify-content-center flex-shrink-0' style={{ width: '20px', height: '20px', fontSize: '10px', backgroundColor: '#fff', color: bwtsBadge.color, border: bwtsBadge.border }}>{idx + 2}</span>
                                <span className='fw-semibold fs-8' style={{ color: bwtsBadge.color }}>{bwtsDisplayName}</span>
                              </div>
                            )
                          })()
                        ) : (
                          <button
                            type='button'
                            className='btn btn-sm btn-icon btn-light-danger'
                            onClick={() => removeMakeReplica('bwtsMakeTypeCapacity', idx)}
                            title='Remove'
                          >
                            <i className='bi bi-x-lg'></i>
                          </button>
                        )}
                      </div>

                      <div className='row'>
                        <div className='col-md-12 mb-4'>
                          {isReadOnly ? null : (
                            <input
                              type='text'
                              className='form-control form-control-sm text-dark mb-1'
                              style={{ backgroundColor: '#fff', maxWidth: 320 }}
                              value={bwtsDisplayName}
                              disabled={false}
                              onChange={(e) => setMakeReplicaLabel('bwtsMakeTypeCapacity', idx, e.target.value)}
                            />
                          )}
                          <div>{renderPrimitiveInput(
                            bwtsMakeField,
                            getReplicaFieldValue(bwtsMakeField.key, idx),
                            (v) => setMakeReplicaValue(bwtsMakeField.key, idx, v)
                          )}</div>
                        </div>

                        <div className='col-md-6 mb-3'>
                          {renderReplicaFieldLabel(bwtsOperationalTestField.label, idx + 1, idx + 2)}
                          <div>{renderPrimitiveInput(
                            bwtsOperationalTestField,
                            getReplicaFieldValue(bwtsOperationalTestField.key, idx),
                            (v) => setMakeReplicaValue(bwtsOperationalTestField.key, idx, v)
                          )}</div>
                        </div>

                        <div className='col-md-6 mb-3'>
                          {renderReplicaFieldLabel(bwtsMinStockField.label, idx + 1, idx + 2)}
                          <div>{renderPrimitiveInput(
                            bwtsMinStockField,
                            getReplicaFieldValue(bwtsMinStockField.key, idx),
                            (v) => setMakeReplicaValue(bwtsMinStockField.key, idx, v)
                          )}</div>
                        </div>

                        <div className='col-md-6 mb-0'>
                          {renderReplicaFieldLabel(bwtsLastShoreServiceField.label, idx + 1, idx + 2)}
                          <div>{renderPrimitiveInput(
                            bwtsLastShoreServiceField,
                            getReplicaFieldValue(bwtsLastShoreServiceField.key, idx),
                            (v) => setMakeReplicaValue(bwtsLastShoreServiceField.key, idx, v)
                          )}</div>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          </>
        )
      }
    }

    return (
      <div className={colClass} key={field.key}>
        <div className='d-flex justify-content-between align-items-center mb-1 gap-2 flex-wrap'>
          {(() => {
            // Determine if this field should show a ① badge in view mode.
            // Case 1: it's a primary replicatable labeled field that has replicas.
            const isPrimaryWithReplicas =
              isReadOnly &&
              field.replicatable &&
              getReplicaArray(form, field.key).length > 0 &&
              LABELED_REPLICA_FIELDS.includes(field.key as (typeof LABELED_REPLICA_FIELDS)[number])
            // Case 2: it's a linked secondary field whose primary has replicas.
            const linkedPrimaryKey =
              isCompressorConditionField || isCompressorPmsField ? 'mac1MakeModelCapacity'
                : isPurifierConditionField || isPurifierPmsField ? 'hfoPurifier1MakeModel'
                  : null
            const isLinkedWithReplicas =
              isReadOnly && linkedPrimaryKey !== null && getReplicaArray(form, linkedPrimaryKey).length > 0

            if (isPrimaryWithReplicas || isLinkedWithReplicas) {
              const b = getBadgeStyle(0)
              return (
                <div className='d-inline-flex align-items-center gap-2 mb-1'>
                  <div className='d-inline-flex align-items-center gap-2 px-2 py-1 rounded-2' style={{ backgroundColor: b.backgroundColor, border: b.border }}>
                    <span className='badge badge-circle fw-bold d-flex align-items-center justify-content-center flex-shrink-0' style={{ width: '20px', height: '20px', fontSize: '10px', backgroundColor: '#fff', color: b.color, border: b.border }}>1</span>
                    <span className='fw-semibold fs-8' style={{ color: b.color }}>{field.label}</span>
                  </div>
                </div>
              )
            }
            return <label className='form-label fw-semibold fs-7 text-dark mb-0'>{field.label}</label>
          })()}
          {showReplicateButton && (
            <button
              type='button'
              className='btn btn-sm btn-light-primary py-1 px-3'
              onClick={() => addMakeReplica(field.key, field.type)}
            >
              <i className='bi bi-plus-lg me-1'></i>
              {buttonLabel}
            </button>
          )}
        </div>
        {renderPrimitiveInput(field, val, (v) => setField(field.key, v))}
        {isCompressorPrimaryField || isPurifierPrimaryField || isBoilerPrimaryField || isIgsPrimaryField || isBwtsPrimaryField ? null : isCompressorConditionField || isPurifierConditionField || isBoilerPerformanceField || isBoilerWaterDateField || isBoilerWaterStatusField || isBoilerWaterChemicalField || isBoilerInspectionField || isIgsPerformanceField || isIgsSensorsField || isIgsDemisterField || isIgsShoreServiceField || isIgsInternalInspectionField || isBwtsOperationalTestField || isBwtsMinStockField || isBwtsLastShoreServiceField ? null : isCompressorPmsField ? (
          replicas.length > 0 &&
          compressorConditionField &&
          compressorPmsField && (
            <div className='mt-3 d-flex flex-column gap-3'>
              {getReplicaArray(form, 'mac1MakeModelCapacity').map((rv, idx) => {
                const compLabel = rv && typeof rv === 'object' && !Array.isArray(rv) && 'label' in rv ? String((rv as any).label ?? '').trim() : ''
                const compDisplayLabel = compLabel || getCompressorReplicaLabel(idx)
                const compBadge = getBadgeStyle(idx + 1)
                return (
                  <div key={`mac1MakeModelCapacity-rep-${idx}`} className='border rounded-3 p-3'>
                    <div className='d-flex justify-content-between align-items-center mb-3 gap-2'>
                      {isReadOnly ? (
                        <div className='d-inline-flex align-items-center gap-2 px-2 py-1 rounded-2' style={{ backgroundColor: compBadge.backgroundColor, border: compBadge.border }}>
                          <span className='badge badge-circle fw-bold d-flex align-items-center justify-content-center flex-shrink-0' style={{ width: '20px', height: '20px', fontSize: '10px', backgroundColor: '#fff', color: compBadge.color, border: compBadge.border }}>{idx + 2}</span>
                          <span className='fw-semibold fs-8' style={{ color: compBadge.color }}>{compDisplayLabel}</span>
                        </div>
                      ) : (
                        <input
                          type='text'
                          className='form-control form-control-sm text-dark'
                          style={{ backgroundColor: '#fff', maxWidth: 260 }}
                          value={compDisplayLabel}
                          disabled={false}
                          onChange={(e) => setMakeReplicaLabel('mac1MakeModelCapacity', idx, e.target.value)}
                        />
                      )}
                      {!isReadOnly && (
                        <button
                          type='button'
                          className='btn btn-sm btn-icon btn-light-danger'
                          onClick={() => removeMakeReplica('mac1MakeModelCapacity', idx)}
                          title='Remove'
                        >
                          <i className='bi bi-x-lg'></i>
                        </button>
                      )}
                    </div>
                    {renderPrimitiveInput(
                      effectiveFormFieldMap.get('mac1MakeModelCapacity')!,
                      rv && typeof rv === 'object' && !Array.isArray(rv) && 'value' in rv
                        ? (rv as any).value
                        : rv,
                      (v) => setMakeReplicaValue('mac1MakeModelCapacity', idx, v)
                    )}
                    <div className='row mt-3'>
                      <div className='col-md-12 mb-3'>
                        {renderReplicaFieldLabel(compressorConditionField.label, idx + 1, idx + 2)}
                        <div>{renderPrimitiveInput(
                          compressorConditionField,
                          getReplicaArray(form, compressorConditionField.key)[idx],
                          (v) => setMakeReplicaValue(compressorConditionField.key, idx, v)
                        )}</div>
                      </div>
                      <div className='col-md-12 mb-0'>
                        {renderReplicaFieldLabel(compressorPmsField.label, idx + 1, idx + 2)}
                        <div>{renderPrimitiveInput(
                          compressorPmsField,
                          getReplicaArray(form, compressorPmsField.key)[idx],
                          (v) => setMakeReplicaValue(compressorPmsField.key, idx, v)
                        )}</div>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )
        ) : isBoilerPluggedPipesField ? (
          getReplicaArray(form, 'boilerMakeTypeCapacity').length > 0 &&
          boilerPerformanceField &&
          boilerWaterDateField &&
          boilerWaterStatusField &&
          boilerWaterChemicalField &&
          boilerInspectionField &&
          boilerPluggedPipesField && (
            <div className='mt-3 d-flex flex-column gap-3'>
              {getReplicaArray(form, 'boilerMakeTypeCapacity').map((rv, idx) => {
                const storedLabel =
                  rv && typeof rv === 'object' && !Array.isArray(rv) && 'label' in rv
                    ? String((rv as any).label ?? '').trim()
                    : ''
                const boilerDisplayName = storedLabel || getBoilerReplicaLabel('boilerMakeTypeCapacity', idx)
                const boilerBadge = getBadgeStyle(idx + 1)
                return (
                  <div key={`boilerMakeTypeCapacity-rep-${idx}`} className='mb-4 pt-2 border-top'>
                    <div className='d-flex justify-content-between align-items-center mb-3 gap-2'>
                      {isReadOnly ? (
                        <div className='d-inline-flex align-items-center gap-2 px-2 py-1 rounded-2' style={{ backgroundColor: boilerBadge.backgroundColor, border: boilerBadge.border }}>
                          <span className='badge badge-circle fw-bold d-flex align-items-center justify-content-center flex-shrink-0' style={{ width: '20px', height: '20px', fontSize: '10px', backgroundColor: '#fff', color: boilerBadge.color, border: boilerBadge.border }}>{idx + 2}</span>
                          <span className='fw-semibold fs-8' style={{ color: boilerBadge.color }}>{boilerDisplayName}</span>
                        </div>
                      ) : (
                        <button
                          type='button'
                          className='btn btn-sm btn-icon btn-light-danger'
                          onClick={() => removeMakeReplica('boilerMakeTypeCapacity', idx)}
                          title='Remove'
                        >
                          <i className='bi bi-x-lg'></i>
                        </button>
                      )}
                    </div>
                    <div className='row'>
                      <div className='col-12 mb-2'>
                        <h6 className='fw-bold fs-6 mb-1' style={{ color: '#009ef7' }}>Boiler Details</h6>
                      </div>
                      <div className='col-md-12 mb-4'>
                        {isReadOnly ? null : (
                          <input
                            type='text'
                            className='form-control form-control-sm text-dark mb-1'
                            style={{ backgroundColor: '#fff', maxWidth: 320 }}
                            value={boilerDisplayName}
                            disabled={false}
                            onChange={(e) => setMakeReplicaLabel('boilerMakeTypeCapacity', idx, e.target.value)}
                          />
                        )}
                        {renderPrimitiveInput(
                          effectiveFormFieldMap.get('boilerMakeTypeCapacity')!,
                          getReplicaFieldValue('boilerMakeTypeCapacity', idx),
                          (v) => setMakeReplicaValue('boilerMakeTypeCapacity', idx, v)
                        )}
                      </div>
                      <div className='col-12 mb-2'>
                        <h6 className='fw-bold fs-6 mb-1' style={{ color: '#009ef7' }}>Performance Tests</h6>
                      </div>
                      <div className='col-md-6 mb-4'>
                        {isReadOnly ? (
                          (() => {
                            const raw = getReplicaArray(form, boilerPerformanceField.key)[idx]
                            const perfLabel = raw && typeof raw === 'object' && !Array.isArray(raw) && 'label' in raw
                              ? String((raw as any).label ?? '').trim()
                              : getBoilerReplicaLabel('boiler1PerformanceTestDatePressure', idx)
                            const b = getBadgeStyle(idx + 1)
                            return (
                              <>
                                <div className='d-inline-flex align-items-center gap-2 mb-2 px-2 py-1 rounded-2' style={{ backgroundColor: b.backgroundColor, border: b.border }}>
                                  <span className='badge badge-circle fw-bold d-flex align-items-center justify-content-center flex-shrink-0' style={{ width: '20px', height: '20px', fontSize: '10px', backgroundColor: '#fff', color: b.color, border: b.border }}>{idx + 2}</span>
                                  <span className='fw-semibold fs-8' style={{ color: b.color }}>{perfLabel}</span>
                                </div>
                                <div>{renderPrimitiveInput(
                                  boilerPerformanceField,
                                  getReplicaFieldValue(boilerPerformanceField.key, idx),
                                  (v) => setMakeReplicaValue(boilerPerformanceField.key, idx, v)
                                )}</div>
                              </>
                            )
                          })()
                        ) : (
                          <>
                            <input
                              type='text'
                              className='form-control form-control-sm text-dark mb-1'
                              style={{ backgroundColor: '#fff' }}
                              value={
                                (() => {
                                  const raw = getReplicaArray(form, boilerPerformanceField.key)[idx];
                                  return raw && typeof raw === 'object' && !Array.isArray(raw) && 'label' in raw
                                    ? String((raw as any).label ?? '')
                                    : getBoilerReplicaLabel('boiler1PerformanceTestDatePressure', idx);
                                })()
                              }
                              disabled={false}
                              onChange={(e) => setMakeReplicaLabel(boilerPerformanceField.key, idx, e.target.value)}
                            />
                            {renderPrimitiveInput(
                              boilerPerformanceField,
                              getReplicaFieldValue(boilerPerformanceField.key, idx),
                              (v) => setMakeReplicaValue(boilerPerformanceField.key, idx, v)
                            )}
                          </>
                        )}
                      </div>
                      <div className='col-12 mb-2'>
                        <h6 className='fw-bold fs-6 mb-1' style={{ color: '#009ef7' }}>Boiler Water Analysis</h6>
                      </div>
                      <div className='col-md-6 mb-3'>
                        {isReadOnly ? (
                          <div className='d-inline-flex align-items-center gap-2 mb-2 px-2 py-1 rounded-2' style={{ backgroundColor: boilerBadge.backgroundColor, border: boilerBadge.border }}>
                            <span className='badge badge-circle fw-bold d-flex align-items-center justify-content-center flex-shrink-0' style={{ width: '20px', height: '20px', fontSize: '10px', backgroundColor: '#fff', color: boilerBadge.color, border: boilerBadge.border }}>{idx + 2}</span>
                            <span className='fw-semibold fs-8' style={{ color: boilerBadge.color }}>{boilerWaterDateField.label}</span>
                          </div>
                        ) : (
                          <label className='form-label fw-semibold fs-7 text-dark'>{boilerWaterDateField.label}</label>
                        )}
                        <div>{renderPrimitiveInput(
                          boilerWaterDateField,
                          getReplicaFieldValue(boilerWaterDateField.key, idx),
                          (v) => setMakeReplicaValue(boilerWaterDateField.key, idx, v)
                        )}</div>
                      </div>
                      <div className='col-md-6 mb-3'>
                        {isReadOnly ? (
                          <div className='d-inline-flex align-items-center gap-2 mb-2 px-2 py-1 rounded-2' style={{ backgroundColor: boilerBadge.backgroundColor, border: boilerBadge.border }}>
                            <span className='badge badge-circle fw-bold d-flex align-items-center justify-content-center flex-shrink-0' style={{ width: '20px', height: '20px', fontSize: '10px', backgroundColor: '#fff', color: boilerBadge.color, border: boilerBadge.border }}>{idx + 2}</span>
                            <span className='fw-semibold fs-8' style={{ color: boilerBadge.color }}>{boilerWaterStatusField.label}</span>
                          </div>
                        ) : (
                          <label className='form-label fw-semibold fs-7 text-dark'>{boilerWaterStatusField.label}</label>
                        )}
                        <div>{renderPrimitiveInput(
                          boilerWaterStatusField,
                          getReplicaFieldValue(boilerWaterStatusField.key, idx),
                          (v) => setMakeReplicaValue(boilerWaterStatusField.key, idx, v)
                        )}</div>
                      </div>
                      <div className='col-md-6 mb-3'>
                        {isReadOnly ? (
                          <div className='d-inline-flex align-items-center gap-2 mb-2 px-2 py-1 rounded-2' style={{ backgroundColor: boilerBadge.backgroundColor, border: boilerBadge.border }}>
                            <span className='badge badge-circle fw-bold d-flex align-items-center justify-content-center flex-shrink-0' style={{ width: '20px', height: '20px', fontSize: '10px', backgroundColor: '#fff', color: boilerBadge.color, border: boilerBadge.border }}>{idx + 2}</span>
                            <span className='fw-semibold fs-8' style={{ color: boilerBadge.color }}>{boilerWaterChemicalField.label}</span>
                          </div>
                        ) : (
                          <label className='form-label fw-semibold fs-7 text-dark'>{boilerWaterChemicalField.label}</label>
                        )}
                        <div>{renderPrimitiveInput(
                          boilerWaterChemicalField,
                          getReplicaFieldValue(boilerWaterChemicalField.key, idx),
                          (v) => setMakeReplicaValue(boilerWaterChemicalField.key, idx, v)
                        )}</div>
                      </div>
                      <div className='col-12 mb-2'>
                        <h6 className='fw-bold fs-6 mb-1' style={{ color: '#009ef7' }}>Internal Inspections</h6>
                      </div>
                      <div className='col-md-6 mb-3'>
                        {isReadOnly ? (
                          (() => {
                            const raw = getReplicaArray(form, boilerInspectionField.key)[idx]
                            const inspLabel = raw && typeof raw === 'object' && !Array.isArray(raw) && 'label' in raw
                              ? String((raw as any).label ?? '').trim()
                              : getBoilerReplicaLabel('lastDateBoiler1InternalInspection', idx)
                            const b = getBadgeStyle(idx + 1)
                            return (
                              <>
                                <div className='d-inline-flex align-items-center gap-2 mb-2 px-2 py-1 rounded-2' style={{ backgroundColor: b.backgroundColor, border: b.border }}>
                                  <span className='badge badge-circle fw-bold d-flex align-items-center justify-content-center flex-shrink-0' style={{ width: '20px', height: '20px', fontSize: '10px', backgroundColor: '#fff', color: b.color, border: b.border }}>{idx + 2}</span>
                                  <span className='fw-semibold fs-8' style={{ color: b.color }}>{inspLabel}</span>
                                </div>
                                <div>{renderPrimitiveInput(
                                  boilerInspectionField,
                                  getReplicaFieldValue(boilerInspectionField.key, idx),
                                  (v) => setMakeReplicaValue(boilerInspectionField.key, idx, v)
                                )}</div>
                              </>
                            )
                          })()
                        ) : (
                          <input
                            type='text'
                            className='form-control form-control-sm text-dark mb-1'
                            style={{ backgroundColor: '#fff' }}
                            value={
                              (() => {
                                const raw = getReplicaArray(form, boilerInspectionField.key)[idx];
                                return raw && typeof raw === 'object' && !Array.isArray(raw) && 'label' in raw
                                  ? String((raw as any).label ?? '')
                                  : getBoilerReplicaLabel('lastDateBoiler1InternalInspection', idx);
                              })()
                            }
                            disabled={false}
                            onChange={(e) => setMakeReplicaLabel(boilerInspectionField.key, idx, e.target.value)}
                          />
                        )}
                        {!isReadOnly && renderPrimitiveInput(
                          boilerInspectionField,
                          getReplicaFieldValue(boilerInspectionField.key, idx),
                          (v) => setMakeReplicaValue(boilerInspectionField.key, idx, v)
                        )}
                      </div>
                      <div className='col-12 mb-2'>
                        <h6 className='fw-bold fs-6 mb-1' style={{ color: '#009ef7' }}>Plugged Pipes</h6>
                      </div>
                      <div className='col-md-6 mb-0'>
                        {isReadOnly ? (
                          (() => {
                            const raw = getReplicaArray(form, boilerPluggedPipesField.key)[idx]
                            const pipesLabel = raw && typeof raw === 'object' && !Array.isArray(raw) && 'label' in raw
                              ? String((raw as any).label ?? '').trim()
                              : getBoilerReplicaLabel('quantityPluggedBoiler1Pipes', idx)
                            const b = getBadgeStyle(idx + 1)
                            return (
                              <>
                                <div className='d-inline-flex align-items-center gap-2 mb-2 px-2 py-1 rounded-2' style={{ backgroundColor: b.backgroundColor, border: b.border }}>
                                  <span className='badge badge-circle fw-bold d-flex align-items-center justify-content-center flex-shrink-0' style={{ width: '20px', height: '20px', fontSize: '10px', backgroundColor: '#fff', color: b.color, border: b.border }}>{idx + 2}</span>
                                  <span className='fw-semibold fs-8' style={{ color: b.color }}>{pipesLabel}</span>
                                </div>
                                <div>{renderPrimitiveInput(
                                  boilerPluggedPipesField,
                                  getReplicaFieldValue(boilerPluggedPipesField.key, idx),
                                  (v) => setMakeReplicaValue(boilerPluggedPipesField.key, idx, v)
                                )}</div>
                              </>
                            )
                          })()
                        ) : (
                          <input
                            type='text'
                            className='form-control form-control-sm text-dark mb-1'
                            style={{ backgroundColor: '#fff' }}
                            value={
                              (() => {
                                const raw = getReplicaArray(form, boilerPluggedPipesField.key)[idx];
                                return raw && typeof raw === 'object' && !Array.isArray(raw) && 'label' in raw
                                  ? String((raw as any).label ?? '')
                                  : getBoilerReplicaLabel('quantityPluggedBoiler1Pipes', idx);
                              })()
                            }
                            disabled={false}
                            onChange={(e) => setMakeReplicaLabel(boilerPluggedPipesField.key, idx, e.target.value)}
                          />
                        )}
                        {!isReadOnly && renderPrimitiveInput(
                          boilerPluggedPipesField,
                          getReplicaFieldValue(boilerPluggedPipesField.key, idx),
                          (v) => setMakeReplicaValue(boilerPluggedPipesField.key, idx, v)
                        )}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )
        ) : isIgsWaterNodesField ? (
          getReplicaArray(form, 'igIgsMakeTypeCapacity').length > 0 &&
          igsMakeField &&
          igsPerformanceField &&
          igsSensorsField &&
          igsDemisterField &&
          igsShoreServiceField &&
          igsInternalInspectionField &&
          igsWaterNodesField && (
            <div className='mt-3 d-flex flex-column gap-3'>
              {getReplicaArray(form, 'igIgsMakeTypeCapacity').map((rv, idx) => {
                const igLabel =
                  rv && typeof rv === 'object' && !Array.isArray(rv) && 'label' in rv
                    ? String((rv as any).label ?? '').trim()
                    : ''
                const igDisplayName = igLabel || getIgsReplicaLabel(idx)
                const perfEntry = getReplicaArray(form, igsPerformanceField.key)[idx]
                const perfLabel =
                  perfEntry && typeof perfEntry === 'object' && !Array.isArray(perfEntry) && 'label' in perfEntry
                    ? String((perfEntry as any).label ?? '').trim()
                    : ''
                const perfDisplayName = perfLabel || getIgsReplicaLabel(idx)

                return (
                  <div key={`igIgsMakeTypeCapacity-rep-${idx}`} className='mb-4 pt-2 border-top'>
                    <div className='d-flex justify-content-between align-items-center mb-3 gap-2'>
                      {isReadOnly ? (
                        (() => {
                          const igsBadge = getBadgeStyle(idx + 1); return (
                            <div className='d-inline-flex align-items-center gap-2 px-2 py-1 rounded-2' style={{ backgroundColor: igsBadge.backgroundColor, border: igsBadge.border }}>
                              <span className='badge badge-circle fw-bold d-flex align-items-center justify-content-center flex-shrink-0' style={{ width: '20px', height: '20px', fontSize: '10px', backgroundColor: '#fff', color: igsBadge.color, border: igsBadge.border }}>{idx + 2}</span>
                              <span className='fw-semibold fs-8' style={{ color: igsBadge.color }}>{igDisplayName}</span>
                            </div>
                          )
                        })()
                      ) : (
                        <button
                          type='button'
                          className='btn btn-sm btn-icon btn-light-danger'
                          onClick={() => removeMakeReplica('igIgsMakeTypeCapacity', idx)}
                          title='Remove'
                        >
                          <i className='bi bi-x-lg'></i>
                        </button>
                      )}
                    </div>

                    <div className='row'>
                      <div className='col-md-12 mb-4'>
                        {!isReadOnly && (
                          <input
                            type='text'
                            className='form-control form-control-sm text-dark mb-1'
                            style={{ backgroundColor: '#fff', maxWidth: 320 }}
                            value={igDisplayName}
                            disabled={false}
                            onChange={(e) => setMakeReplicaLabel('igIgsMakeTypeCapacity', idx, e.target.value)}
                          />
                        )}
                        {renderPrimitiveInput(
                          igsMakeField,
                          getReplicaFieldValue(igsMakeField.key, idx),
                          (v) => setMakeReplicaValue(igsMakeField.key, idx, v)
                        )}
                      </div>

                      <div className='col-md-6 mb-3'>
                        {isReadOnly ? (
                          renderReplicaFieldLabel(igsPerformanceField.label, idx + 1, idx + 2)
                        ) : (
                          <input
                            type='text'
                            className='form-control form-control-sm text-dark mb-1'
                            style={{ backgroundColor: '#fff' }}
                            value={perfDisplayName}
                            disabled={false}
                            onChange={(e) => setMakeReplicaLabel(igsPerformanceField.key, idx, e.target.value)}
                          />
                        )}
                        <div>{renderPrimitiveInput(
                          igsPerformanceField,
                          getReplicaFieldValue(igsPerformanceField.key, idx),
                          (v) => setMakeReplicaValue(igsPerformanceField.key, idx, v)
                        )}</div>
                      </div>

                      <div className='col-md-6 mb-3'>
                        {renderReplicaFieldLabel(igsSensorsField.label, idx + 1, idx + 2)}
                        <div>{renderPrimitiveInput(
                          igsSensorsField,
                          getReplicaFieldValue(igsSensorsField.key, idx),
                          (v) => setMakeReplicaValue(igsSensorsField.key, idx, v)
                        )}</div>
                      </div>

                      <div className='col-md-6 mb-3'>
                        {renderReplicaFieldLabel(igsDemisterField.label, idx + 1, idx + 2)}
                        <div>{renderPrimitiveInput(
                          igsDemisterField,
                          getReplicaFieldValue(igsDemisterField.key, idx),
                          (v) => setMakeReplicaValue(igsDemisterField.key, idx, v)
                        )}</div>
                      </div>

                      <div className='col-md-6 mb-3'>
                        {renderReplicaFieldLabel(igsShoreServiceField.label, idx + 1, idx + 2)}
                        <div>{renderPrimitiveInput(
                          igsShoreServiceField,
                          getReplicaFieldValue(igsShoreServiceField.key, idx),
                          (v) => setMakeReplicaValue(igsShoreServiceField.key, idx, v)
                        )}</div>
                      </div>

                      <div className='col-md-6 mb-3'>
                        {renderReplicaFieldLabel(igsInternalInspectionField.label, idx + 1, idx + 2)}
                        <div>{renderPrimitiveInput(
                          igsInternalInspectionField,
                          getReplicaFieldValue(igsInternalInspectionField.key, idx),
                          (v) => setMakeReplicaValue(igsInternalInspectionField.key, idx, v)
                        )}</div>
                      </div>

                      <div className='col-md-6 mb-0'>
                        {renderReplicaFieldLabel(igsWaterNodesField.label, idx + 1, idx + 2)}
                        <div>{renderPrimitiveInput(
                          igsWaterNodesField,
                          getReplicaFieldValue(igsWaterNodesField.key, idx),
                          (v) => setMakeReplicaValue(igsWaterNodesField.key, idx, v)
                        )}</div>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )
        ) : isPurifierPmsField ? (
          replicas.length > 0 &&
          purifierConditionField &&
          purifierPmsField && (
            <div className='mt-3 d-flex flex-column gap-3'>
              {getReplicaArray(form, 'hfoPurifier1MakeModel').map((rv, idx) => {
                const purLabel = rv && typeof rv === 'object' && !Array.isArray(rv) && 'label' in rv ? String((rv as any).label ?? '').trim() : ''
                const purDisplayLabel = purLabel || getPurifierReplicaLabel(idx)
                const purBadge = getBadgeStyle(idx + 1)
                return (
                  <div key={`hfoPurifier1MakeModel-rep-${idx}`} className='border rounded-3 p-3'>
                    <div className='d-flex justify-content-between align-items-center mb-3 gap-2'>
                      {isReadOnly ? (
                        <div className='d-inline-flex align-items-center gap-2 px-2 py-1 rounded-2' style={{ backgroundColor: purBadge.backgroundColor, border: purBadge.border }}>
                          <span className='badge badge-circle fw-bold d-flex align-items-center justify-content-center flex-shrink-0' style={{ width: '20px', height: '20px', fontSize: '10px', backgroundColor: '#fff', color: purBadge.color, border: purBadge.border }}>{idx + 2}</span>
                          <span className='fw-semibold fs-8' style={{ color: purBadge.color }}>{purDisplayLabel}</span>
                        </div>
                      ) : (
                        <input
                          type='text'
                          className='form-control form-control-sm text-dark'
                          style={{ backgroundColor: '#fff', maxWidth: 260 }}
                          value={purDisplayLabel}
                          disabled={false}
                          onChange={(e) => setMakeReplicaLabel('hfoPurifier1MakeModel', idx, e.target.value)}
                        />
                      )}
                      {!isReadOnly && (
                        <button
                          type='button'
                          className='btn btn-sm btn-icon btn-light-danger'
                          onClick={() => removeMakeReplica('hfoPurifier1MakeModel', idx)}
                          title='Remove'
                        >
                          <i className='bi bi-x-lg'></i>
                        </button>
                      )}
                    </div>
                    <div>{renderPrimitiveInput(
                      effectiveFormFieldMap.get('hfoPurifier1MakeModel')!,
                      rv && typeof rv === 'object' && !Array.isArray(rv) && 'value' in rv
                        ? (rv as any).value
                        : rv,
                      (v) => setMakeReplicaValue('hfoPurifier1MakeModel', idx, v)
                    )}</div>
                    <div className='row mt-3'>
                      <div className='col-md-12 mb-3'>
                        {renderReplicaFieldLabel(purifierConditionField.label, idx + 1, idx + 2)}
                        <div>{renderPrimitiveInput(
                          purifierConditionField,
                          getReplicaArray(form, purifierConditionField.key)[idx],
                          (v) => setMakeReplicaValue(purifierConditionField.key, idx, v)
                        )}</div>
                      </div>
                      <div className='col-md-12 mb-0'>
                        {renderReplicaFieldLabel(purifierPmsField.label, idx + 1, idx + 2)}
                        <div>{renderPrimitiveInput(
                          purifierPmsField,
                          getReplicaArray(form, purifierPmsField.key)[idx],
                          (v) => setMakeReplicaValue(purifierPmsField.key, idx, v)
                        )}</div>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )
        ) : (
          replicas.length > 0 && (
            <div className='mt-2'>
              {replicas.map((rv, idx) => {
                const replicaNumber = idx + 2 // AE #1 is the base field, replicas start at #2
                const badgeStyle = getBadgeStyle(replicaNumber - 1)
                const replicaLabel =
                  rv && typeof rv === 'object' && !Array.isArray(rv) && 'label' in rv
                    ? String((rv as any).label ?? '').trim()
                    : isAuxEngineReplicaField(field.key)
                      ? getAuxEngineReplicaLabel(idx)
                      : `Field ${replicaNumber}`
                const displayLabel = replicaLabel || (isAuxEngineReplicaField(field.key) ? getAuxEngineReplicaLabel(idx) : `Field ${replicaNumber}`)

                return (
                  <div className='d-flex align-items-start gap-2 mb-2' key={`${field.key}-rep-${idx}`}>
                    <div className='flex-grow-1 d-flex flex-column'>
                      {/* Numbered badge header — shown in both view and edit mode */}
                      {(LABELED_REPLICA_FIELDS.includes(field.key as (typeof LABELED_REPLICA_FIELDS)[number]) ||
                        field.key === 'ae1MaxLoad') && (
                          isReadOnly ? (
                            <div
                              className='d-inline-flex align-items-center gap-2 mb-2 px-2 py-1 rounded-2'
                              style={{
                                backgroundColor: badgeStyle.backgroundColor,
                                border: badgeStyle.border,
                                minWidth: 80,
                                alignSelf: 'flex-start',
                              }}
                            >
                              <span
                                className='badge badge-circle fw-bold d-flex align-items-center justify-content-center flex-shrink-0'
                                style={{
                                  width: '20px',
                                  height: '20px',
                                  fontSize: '10px',
                                  backgroundColor: '#fff',
                                  color: badgeStyle.color,
                                  border: badgeStyle.border,
                                }}
                              >
                                {replicaNumber}
                              </span>
                              <span className='fw-semibold fs-8' style={{ color: badgeStyle.color }}>
                                {displayLabel}
                              </span>
                            </div>
                          ) : (
                            <input
                              type='text'
                              className='form-control form-control-sm text-dark mb-1'
                              style={{ backgroundColor: '#fff' }}
                              value={displayLabel}
                              disabled={false}
                              onChange={(e) => setMakeReplicaLabel(field.key, idx, e.target.value)}
                            />
                          )
                        )}
                      {['dailyConsumptionVlsfoMgo', 'dateOfLastMaintenance'].includes(field.key) && (
                        isReadOnly
                          ? renderReplicaFieldLabel(field.label, replicaNumber - 1, replicaNumber)
                          : <label className='form-label fw-semibold fs-7 text-dark'>{field.label}</label>
                      )}
                      <div>{renderPrimitiveInput(
                        field,
                        rv && typeof rv === 'object' && !Array.isArray(rv) && 'value' in rv
                          ? (rv as any).value
                          : rv,
                        (v) => setMakeReplicaValue(field.key, idx, v)
                      )}</div>
                    </div>
                    {showReplicaRemoveButton && (
                      <button
                        type='button'
                        className='btn btn-sm btn-icon btn-light-danger'
                        onClick={() => removeMakeReplica(field.key, idx)}
                        title='Remove'
                      >
                        <i className='bi bi-x-lg'></i>
                      </button>
                    )}
                  </div>
                )
              })}
            </div>
          )
        )}
      </div>
    )
  }

  const renderRepeatableEntryField = (
    sectionId: string,
    field: RepeatableEntryFieldDef,
    idx: number,
    entry: Record<string, any>
  ) => {
    const colClass = `col-md-${field.colSpan || 6} mb-3`
    const val = entry?.[field.key] ?? ''
    const commonProps = {
      className: 'form-control form-control-sm text-dark',
      style: { backgroundColor: '#fff' },
      disabled: isReadOnly,
    } as const

    if (field.type === 'select') {
      return (
        <div className={colClass} key={field.key}>
          <label className='form-label fw-semibold fs-7 text-dark' title={field.label}>{field.label}</label>
          <SearchableSelect
            className='form-select form-select-sm text-dark'
            style={{ backgroundColor: '#fff' }}
            value={val}
            disabled={isReadOnly}
            onChange={(e) => setExtraEntryField(sectionId, idx, field.key, e.target.value || null)}
          >
            <option value=''>Select...</option>
            {(field.options || []).map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </SearchableSelect>
        </div>
      )
    }

    if (field.type === 'textarea') {
      return (
        <div className={colClass} key={field.key}>
          <label className='form-label fw-semibold fs-7 text-dark' title={field.label}>{field.label}</label>
          <textarea
            {...commonProps}
            rows={2}
            value={val}
            onChange={(e) => setExtraEntryField(sectionId, idx, field.key, e.target.value)}
          />
        </div>
      )
    }

    return (
      <div className={colClass} key={field.key}>
        <label className='form-label fw-semibold fs-7 text-dark' title={field.label}>{field.label}</label>
        <input
          {...commonProps}
          type={field.type}
          step={field.type === 'number' ? 'any' : undefined}
          value={val}
          onChange={(e) =>
            setExtraEntryField(
              sectionId,
              idx,
              field.key,
              field.type === 'number'
                ? e.target.value
                  ? Number(e.target.value)
                  : null
                : e.target.value
            )
          }
        />
      </div>
    )
  }

  const renderPeriodFilters = () => {
    if (isGlobalScope) {
      return (
        <>
          <div className={isGlobalScope ? (showFilterSubCompany ? 'col-md-3' : 'col-md-4') : 'col-md-6'}>
            <label className='form-label fw-bold text-dark'>Vessel</label>
            <SearchableSelect
              className='form-select form-select-sm'
              value={filterVessel}
              onChange={(e) => setFilterVessel(e.target.value)}
              disabled={isShipboardUser}
            >
              {!isShipboardUser && <option value=''>All Vessels</option>}
              {vessels.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.fleet_name || v.name}
                </option>
              ))}
            </SearchableSelect>
          </div>
          {renderMonthMultiSelectDropdown(showFilterSubCompany ? 'col-md-3' : 'col-md-4')}
        </>
      )
    }

    if (useMultiSelectFilters) {
      if (canMultiMonthForShipboard) {
        return (
          <div className='col-md-4'>
            <label className='form-label fw-bold text-dark'>Months Filter</label>
            <div className='position-relative'>
              <button
                type='button'
                className='form-select form-select-sm text-start'
                onClick={() => setCompareMonthDropdownOpen((o) => !o)}
              >
                {filterMonthKeys.length > 0 ? `${filterMonthKeys.length} month(s) selected` : 'Select months'}
              </button>
              {compareMonthDropdownOpen && (
                <div
                  className='position-absolute bg-white border rounded shadow-sm w-100 mt-1 p-2'
                  style={{ zIndex: 20, maxHeight: 280, overflowY: 'auto' }}
                >
                  <div className='d-flex flex-column gap-3'>
                    <div className='d-flex align-items-center justify-content-between bg-light rounded p-2 border'>
                      <button
                        type='button'
                        className='btn btn-xs btn-icon btn-active-light-primary'
                        onClick={() => setCompareViewYear((prev) => prev - 1)}
                      >
                        <i className='bi bi-chevron-left'></i>
                      </button>
                      <div className='d-flex align-items-center gap-2'>
                        <input
                          type='number'
                          className='form-control form-control-sm py-0 px-1 text-center fw-bolder border-0 bg-transparent'
                          style={{ width: '60px', fontSize: '15px' }}
                          value={compareViewYear}
                          onChange={(e) => setCompareViewYear(Number(e.target.value))}
                        />
                        <button
                          className='btn btn-xs btn-link p-0 fs-9 text-decoration-none'
                          onClick={() => {
                            const yearKeys = MONTHS_SHORT.map(
                              (_, idx) => `${compareViewYear}-${String(idx + 1).padStart(2, '0')}`
                            )
                            const allSelected = yearKeys.every((k) => filterMonthKeys.includes(k))
                            if (allSelected) {
                              handleMonthMultiSelectChange(filterMonthKeys.filter((k) => !yearKeys.includes(k)))
                            } else {
                              handleMonthMultiSelectChange(Array.from(new Set([...filterMonthKeys, ...yearKeys])))
                            }
                          }}
                        >
                          Toggle All
                        </button>
                      </div>
                      <button
                        type='button'
                        className='btn btn-xs btn-icon btn-active-light-primary'
                        onClick={() => setCompareViewYear((prev) => prev + 1)}
                      >
                        <i className='bi bi-chevron-right'></i>
                      </button>
                    </div>
                    <div className='d-grid' style={{ gridTemplateColumns: 'repeat(3, 1fr)', gap: '6px' }}>
                      {MONTHS_SHORT.map((mLabel, idx) => {
                        const mk = `${compareViewYear}-${String(idx + 1).padStart(2, '0')}`
                        const selected = filterMonthKeys.includes(mk)
                        return (
                          <button
                            key={mk}
                            type='button'
                            className={`btn btn-sm py-2 px-0 border transition-all d-flex flex-column align-items-center justify-content-center ${selected
                              ? 'btn-primary border-primary shadow-sm'
                              : 'btn-light border-gray-200 text-muted hover:border-primary'
                              }`}
                            style={{ fontSize: '11px' }}
                            onClick={() => {
                              const next = selected
                                ? filterMonthKeys.filter((k) => k !== mk)
                                : [...filterMonthKeys, mk]
                              handleMonthMultiSelectChange(next)
                            }}
                          >
                            <span className='fw-bold'>{mLabel}</span>
                          </button>
                        )
                      })}
                    </div>
                    {filterMonthKeys.length > 0 && (
                      <div className='mt-2 border-top pt-3'>
                        <div className='d-flex justify-content-between align-items-center mb-2'>
                          <label className='fs-9 fw-bold text-muted text-uppercase'>Selected Periods</label>
                          <button
                            className='btn btn-xs btn-link p-0 fs-9'
                            onClick={() => handleMonthMultiSelectChange([])}
                          >
                            Clear All
                          </button>
                        </div>
                        <div className='d-flex flex-wrap gap-1' style={{ maxHeight: '100px', overflowY: 'auto' }}>
                          {Array.from(new Set(filterMonthKeys.map((k) => k.split('-')[0])))
                            .sort((a, b) => (a < b ? 1 : -1))
                            .map((year) => {
                              const yearMonths = filterMonthKeys
                                .filter((k) => k.startsWith(year + '-'))
                                .map((k) => MONTHS_SHORT[Number(k.split('-')[1]) - 1])
                                .join(', ')
                              return (
                                <div
                                  key={year}
                                  className='badge badge-light-primary d-flex align-items-center gap-1 py-1 px-2 border border-primary border-opacity-10'
                                >
                                  <span className='fw-bold'>{year}:</span>
                                  <span
                                    className='text-dark fs-9'
                                    style={{ maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                    title={yearMonths}
                                  >
                                    {yearMonths}
                                  </span>
                                  <i
                                    className='bi bi-x fs-7 cursor-pointer hover:text-danger'
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      handleMonthMultiSelectChange(
                                        filterMonthKeys.filter((k) => !k.startsWith(year + '-'))
                                      )
                                    }}
                                  ></i>
                                </div>
                              )
                            })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )
      }

      return (
        <>
          <div className={isGlobalScope ? (showFilterSubCompany ? 'col-md-3' : 'col-md-4') : 'col-md-6'}>
            <label className='form-label fw-bold text-dark'>Vessel Selection</label>
            <div className='position-relative'>
              <button
                type='button'
                className='form-select form-select-sm text-start'
                onClick={() => {
                  setVesselDropdownOpen((o) => !o)
                  setCompareMonthDropdownOpen(false)
                }}
              >
                {filterVesselIds.length === 0
                  ? 'All Vessels'
                  : `${filterVesselIds.length} vessel(s) selected`}
              </button>
              {vesselDropdownOpen && (
                <div
                  className='position-absolute bg-white border rounded shadow-sm w-100 mt-1 p-2'
                  style={{ zIndex: 20, maxHeight: 260, overflowY: 'auto' }}
                >
                  {vessels.length === 0 ? (
                    <div className='p-2 text-muted text-center fs-8'>{NO_ACCESSIBLE_VESSELS_MESSAGE}</div>
                  ) : (
                    vessels.map((v) => {
                      const vid = Number(v.id)
                      const checked = filterVesselIds.includes(vid)
                      return (
                        <div
                          key={vid}
                          className='form-check mb-1'
                          onClick={() =>
                            handleVesselMultiSelectChange(
                              checked ? filterVesselIds.filter((x) => x !== vid) : [...filterVesselIds, vid]
                            )
                          }
                          style={{ cursor: 'pointer' }}
                        >
                          <input
                            type='checkbox'
                            className='form-check-input'
                            checked={checked}
                            readOnly
                          />
                          <label className='form-check-label fs-8'>{v.fleet_name || v.name}</label>
                        </div>
                      )
                    })
                  )}
                </div>
              )}
            </div>
            {filterSummaryMessage && (
              <div className='alert alert-light-info border border-info border-opacity-10 d-flex align-items-start gap-2 py-2 px-3 mt-2 mb-0 fs-8 text-dark'>
                <i className='bi bi-info-circle-fill text-info mt-1'></i>
                <span>{filterSummaryMessage}</span>
              </div>
            )}
          </div>
          <div className={isGlobalScope ? (showFilterSubCompany ? 'col-md-3' : 'col-md-4') : 'col-md-6'}>
            <label className='form-label fw-bold text-dark'>Months Filter</label>
            <div className='position-relative'>
              <button
                type='button'
                className='form-select form-select-sm text-start'
                onClick={() => {
                  setCompareMonthDropdownOpen((o) => !o)
                  setVesselDropdownOpen(false)
                }}
              >
                {filterMonthKeys.length > 0 ? `${filterMonthKeys.length} month(s) selected` : 'Select months'}
              </button>
              {compareMonthDropdownOpen && (
                <div
                  className='position-absolute bg-white border rounded shadow-sm w-100 mt-1 p-2'
                  style={{ zIndex: 20, maxHeight: 280, overflowY: 'auto' }}
                >
                  <div className='d-flex flex-column gap-3'>
                    <div className='d-flex align-items-center justify-content-between bg-light rounded p-2 border'>
                      <button
                        type='button'
                        className='btn btn-xs btn-icon btn-active-light-primary'
                        onClick={() => setCompareViewYear((prev) => prev - 1)}
                      >
                        <i className='bi bi-chevron-left'></i>
                      </button>
                      <div className='d-flex align-items-center gap-2'>
                        <input
                          type='number'
                          className='form-control form-control-sm py-0 px-1 text-center fw-bolder border-0 bg-transparent'
                          style={{ width: '60px', fontSize: '15px' }}
                          value={compareViewYear}
                          onChange={(e) => setCompareViewYear(Number(e.target.value))}
                        />
                        <button
                          className='btn btn-xs btn-link p-0 fs-9 text-decoration-none'
                          onClick={() => {
                            const yearKeys = MONTHS_SHORT.map(
                              (_, idx) => `${compareViewYear}-${String(idx + 1).padStart(2, '0')}`
                            )
                            const allSelected = yearKeys.every((k) => filterMonthKeys.includes(k))
                            if (allSelected) {
                              handleMonthMultiSelectChange(filterMonthKeys.filter((k) => !yearKeys.includes(k)))
                            } else {
                              handleMonthMultiSelectChange(Array.from(new Set([...filterMonthKeys, ...yearKeys])))
                            }
                          }}
                        >
                          Toggle All
                        </button>
                      </div>
                      <button
                        type='button'
                        className='btn btn-xs btn-icon btn-active-light-primary'
                        onClick={() => setCompareViewYear((prev) => prev + 1)}
                      >
                        <i className='bi bi-chevron-right'></i>
                      </button>
                    </div>
                    <div className='d-grid' style={{ gridTemplateColumns: 'repeat(3, 1fr)', gap: '6px' }}>
                      {MONTHS_SHORT.map((mLabel, idx) => {
                        const mk = `${compareViewYear}-${String(idx + 1).padStart(2, '0')}`
                        const selected = filterMonthKeys.includes(mk)
                        return (
                          <button
                            key={mk}
                            type='button'
                            className={`btn btn-sm py-2 px-0 border transition-all d-flex flex-column align-items-center justify-content-center ${selected
                              ? 'btn-primary border-primary shadow-sm'
                              : 'btn-light border-gray-200 text-muted hover:border-primary'
                              }`}
                            style={{ fontSize: '11px' }}
                            onClick={() => {
                              const next = selected
                                ? filterMonthKeys.filter((k) => k !== mk)
                                : [...filterMonthKeys, mk]
                              handleMonthMultiSelectChange(next)
                            }}
                          >
                            <span className='fw-bold'>{mLabel}</span>
                          </button>
                        )
                      })}
                    </div>
                    {filterMonthKeys.length > 0 && (
                      <div className='mt-2 border-top pt-3'>
                        <div className='d-flex justify-content-between align-items-center mb-2'>
                          <label className='fs-9 fw-bold text-muted text-uppercase'>Selected Periods</label>
                          <button
                            className='btn btn-xs btn-link p-0 fs-9'
                            onClick={() => handleMonthMultiSelectChange([])}
                          >
                            Clear All
                          </button>
                        </div>
                        <div className='d-flex flex-wrap gap-1' style={{ maxHeight: '100px', overflowY: 'auto' }}>
                          {Array.from(new Set(filterMonthKeys.map((k) => k.split('-')[0])))
                            .sort((a, b) => (a < b ? 1 : -1))
                            .map((year) => {
                              const yearMonths = filterMonthKeys
                                .filter((k) => k.startsWith(year + '-'))
                                .map((k) => MONTHS_SHORT[Number(k.split('-')[1]) - 1])
                                .join(', ')
                              return (
                                <div
                                  key={year}
                                  className='badge badge-light-primary d-flex align-items-center gap-1 py-1 px-2 border border-primary border-opacity-10'
                                >
                                  <span className='fw-bold'>{year}:</span>
                                  <span
                                    className='text-dark fs-9'
                                    style={{ maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                    title={yearMonths}
                                  >
                                    {yearMonths}
                                  </span>
                                  <i
                                    className='bi bi-x fs-7 cursor-pointer hover:text-danger'
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      handleMonthMultiSelectChange(
                                        filterMonthKeys.filter((k) => !k.startsWith(year + '-'))
                                      )
                                    }}
                                  ></i>
                                </div>
                              )
                            })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )
    }

    return (
      <>
        {!canMultiMonthForShipboard && (
          <div className={isGlobalScope ? (showFilterSubCompany ? 'col-md-3' : 'col-md-4') : 'col-md-4'}>
            <label className='form-label fw-bold text-dark'>Vessel</label>
            <SearchableSelect
              className='form-select form-select-sm'
              value={filterVessel}
              onChange={(e) => setFilterVessel(e.target.value)}
              disabled={isShipboardUser}
            >
              {!isShipboardUser && <option value=''>All Vessels</option>}
              {vessels.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.fleet_name || v.name}
                </option>
              ))}
            </SearchableSelect>
            {filterSummaryMessage && (
              <div className='alert alert-light-info border border-info border-opacity-10 d-flex align-items-start gap-2 py-2 px-3 mt-2 mb-0 fs-8 text-dark'>
                <i className='bi bi-info-circle-fill text-info mt-1'></i>
                <span>{filterSummaryMessage}</span>
              </div>
            )}
          </div>
        )}
        <div
          className={
            isGlobalScope
              ? showFilterSubCompany
                ? canMultiMonthForShipboard
                  ? 'col-md-3'
                  : 'col-md-2'
                : canMultiMonthForShipboard
                  ? 'col-md-4'
                  : 'col-md-3'
              : 'col-md-4'
          }
        >
          <label className='form-label fw-bold text-dark'>Month/Year</label>
          <input
            type='month'
            className='form-control form-control-sm'
            style={{ maxWidth: '50%' }}
            value={filterMonthValue}
            onChange={(e) => setFilterMonthValue(e.target.value)}
          />
        </div>
      </>
    )
  }

  return accessDenied ? (
    <QhseAccessDenied
      message={`Access denied - you do not have permission to view ${config.title}.`}
    />
  ) : (
    <>
      <div className='card'>
        <div className='card-header border-0 pt-6 d-flex justify-content-between bg-white'>
          <div>
            <h3 className='card-label text-dark fw-bold'>{config.title}</h3>
            <span className='text-muted fs-7'>{config.subtitle}</span>
          </div>
          <div className='card-toolbar d-flex align-items-center gap-3'>
            {showAddButton && canAdd && (
              <button className='btn btn_primary' onClick={() => void handleOpenAdd()}>
                <i className='bi bi-plus-lg me-1'></i>Add Report
              </button>
            )}
          </div>
        </div>
        <div className='card-body py-5 bg-white border-top'>
          <div className='border rounded-3 p-4'>
            <div className='row g-3 align-items-end'>
              {renderPeriodFilters()}
            </div>
          </div>
        </div>
        <div className='px-5 pb-5 bg-white'>
          <div className='report-table border rounded-3 overflow-hidden bg-white shadow-sm'>
            <div style={{ overflowX: 'auto' }}>
              <table className='table table-bordered align-middle'>
                <thead className='table-header text-start'>
                  <tr>
                    {showVesselColumn && <th className='ps-4 text-nowrap'>Vessel</th>}
                    <th className='text-nowrap'>Month/Year</th>
                    {config.columns.map((col) => (
                      <th key={col.key} className='text-nowrap'>
                        {col.header}
                      </th>
                    ))}
                    <th className='text-nowrap'>Status</th>
                    <th className='text-end pe-4 text-nowrap'>Actions</th>
                  </tr>
                </thead>
                <tbody className='table-body text-start'>
                  {loading ? (
                    <TableSkeleton
                      rows={rowsPerPage || 8}
                      cols={config.columns.length + (showVesselColumn ? 4 : 3)}
                    />
                  ) : pageRecords.length === 0 ? (
                    <tr>
                      <td
                        colSpan={config.columns.length + (showVesselColumn ? 4 : 3)}
                        className='text-center py-10 text-muted'
                      >
                        No reports found
                      </td>
                    </tr>
                  ) : (
                    pageRecords.map((r: any) => (
                      <tr key={r.id}>
                        {showVesselColumn && <td className='ps-4 fw-semibold'>{r.vesselName || '-'}</td>}
                        <td>
                          {r.reportYear && r.reportMonth
                            ? new Date(r.reportYear, r.reportMonth - 1).toLocaleString('default', {
                              month: 'short',
                            }) +
                            ' ' +
                            r.reportYear
                            : '-'}
                        </td>
                        {config.columns.map((col) => (
                          <td key={col.key}>{renderColumnCell(col, r)}</td>
                        ))}
                        <td>
                          <span
                            className={`badge badge-light-${r.status === 'REVIEWED'
                              ? 'success'
                              : r.status === 'SUBMITTED'
                                ? 'warning'
                                : 'primary'
                              } fw-bold`}
                          >
                            {r.status || 'DRAFT'}
                          </span>
                        </td>
                        <td className='text-end pe-4 text-nowrap'>
                          <div className='d-flex gap-1 justify-content-end align-items-center'>
                            {!isContinuationPlaceholder(r) && canDownloadPdf(r) && (
                              <button
                                className='btn btn-icon btn-sm'
                                title='Download PDF'
                                onClick={() => onDownloadPdf!(r)}
                              >
                                <KTSVG
                                  path='/media/icons/duotune/general/download.svg'
                                  className='svg-icon-3 text-success'
                                />
                              </button>
                            )}
                            {!isContinuationPlaceholder(r) && (
                              <button
                                className='btn btn-icon btn-sm'
                                title='View'
                                onClick={() => handleOpenView(r)}
                              >
                                <KTSVG
                                  path='/media/map/ph_eye.svg'
                                  className='svg-icon-3 text-primary'
                                />
                              </button>
                            )}
                            {canEditRow(r) && (
                              <button
                                className='btn btn-icon btn-sm'
                                title={isContinuationPlaceholder(r) ? 'Continue' : 'Edit'}
                                onClick={() => handleOpenEdit(r)}
                              >
                                <KTSVG path='/media/map/edit-active.svg' className='svg-icon-3' />
                              </button>
                            )}
                            {!isContinuationPlaceholder(r) && canDeleteRow(r) && (
                              <button
                                className='btn btn-icon btn-sm'
                                title='Delete'
                                onClick={() => setDeletingRecord(r)}
                              >
                                <KTSVG
                                  path='/media/map/red-cross-icon.svg'
                                  className='svg-icon-3'
                                />
                              </button>
                            )}
                            {!isContinuationPlaceholder(r) && canSubmitRow(r) && (
                              <button
                                className='btn btn-light-primary btn-sm'
                                onClick={() => setSubmitTargetId(Number(r.id))}
                              >
                                Submit
                              </button>
                            )}
                            {!isContinuationPlaceholder(r) && canReviewRow(r) && (
                              <button
                                className='btn btn-light-success btn-sm'
                                onClick={() => setReviewingRecord(r)}
                              >
                                Review
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
          {filteredRecords.length > 0 && (
            <div
              className='pagination-wrapper d-flex justify-content-between align-items-center py-3 border-top px-4'
              style={{ position: 'static', bottom: 0, backgroundColor: '#fff', zIndex: 10 }}
            >
              <div className='d-flex align-items-center'>
                <span className='text-muted me-2 fs-7'>Rows per page</span>
                <SearchableSelect
                  className='form-select'
                  style={{
                    borderRadius: '20px',
                    width: '70px',
                    border: '1px solid #dee2e6',
                    fontSize: '14px',
                    padding: '4px 8px',
                  }}
                  value={rowsPerPage}
                  onChange={(e) => {
                    setRowsPerPage(Number(e.target.value))
                    setCurrentPage(1)
                  }}
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </SearchableSelect>
              </div>
              <div className='d-flex align-items-center'>
                <span className='text-muted me-3 fs-7'>
                  Showing{' '}
                  <strong>
                    {filteredRecords.length ? (currentPage - 1) * rowsPerPage + 1 : 0}-
                    {Math.min(currentPage * rowsPerPage, filteredRecords.length)}
                  </strong>{' '}
                  of <strong>{filteredRecords.length}</strong>
                </span>
                <nav>
                  <ul className='pagination pagination-sm mb-0' style={{ gap: '2px' }}>
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                      <button
                        className='page-link text-muted'
                        style={{
                          backgroundColor: '#f8f9fa',
                          border: '1px solid #dee2e6',
                          padding: '8px 12px',
                          fontSize: '14px',
                          borderRadius: '6px',
                        }}
                        onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                      >
                        &lsaquo;
                      </button>
                    </li>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((i) => (
                      <li key={i} className={`page-item ${currentPage === i ? 'active' : ''}`}>
                        <button
                          className='page-link text-muted'
                          style={{
                            backgroundColor: currentPage === i ? '#F4F9FF' : 'transparent',
                            border: '1px solid #dee2e6',
                            padding: '8px 12px',
                            fontSize: '14px',
                            minWidth: '40px',
                            borderRadius: '6px',
                            outline: 'none',
                            boxShadow: 'none',
                          }}
                          onClick={() => setCurrentPage(i)}
                        >
                          {i}
                        </button>
                      </li>
                    ))}
                    <li className={`page-item ${currentPage >= totalPages ? 'disabled' : ''}`}>
                      <button
                        className='page-link text-muted'
                        style={{
                          backgroundColor: '#f8f9fa',
                          border: '1px solid #dee2e6',
                          padding: '8px 12px',
                          fontSize: '14px',
                          borderRadius: '6px',
                        }}
                        onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                        disabled={currentPage >= totalPages}
                      >
                        &rsaquo;
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add / Edit / View Modal */}
      {modalMode && (
        <div
          className='modal fade show d-block'
          tabIndex={-1}
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
        >
          <div className='modal-dialog modal-xl modal-dialog-scrollable'>
            <div className='modal-content'>
              <div className='modal-header'>
                <div className='d-flex flex-column flex-grow-1'>
                  <h5 className='modal-title fw-bold mb-0'>
                    {modalMode === 'add'
                      ? 'Add Report'
                      : modalMode === 'edit'
                        ? 'Edit Report'
                        : 'View Report'}
                  </h5>
                  {modalMode === 'view' && (
                    <div className='text-muted fs-8 mt-1'>
                      Vessel: <span className='fw-bold text-dark'>{form.vesselName || '-'}</span>
                      <span className='mx-2'>•</span>
                      Month: <span className='fw-bold text-dark'>
                        {form.reportYear && form.reportMonth
                          ? MONTHS_SHORT[form.reportMonth - 1] + ' ' + form.reportYear
                          : '-'}
                      </span>
                    </div>
                  )}
                </div>
                <div className='d-flex align-items-center gap-3'>
                  {modalMode === 'view' && (
                    <span
                      className={`badge badge-light-${form.status === 'REVIEWED'
                        ? 'success'
                        : form.status === 'SUBMITTED'
                          ? 'warning'
                          : 'primary'
                        } fw-bold`}
                    >
                      {form.status || 'DRAFT'}
                    </span>
                  )}
                  <button
                    type='button'
                    className='btn-close'
                    onClick={() => setModalMode(null)}
                  ></button>
                </div>
              </div>
              <div className='modal-body' style={{ position: 'relative' }}>
                {wizardLoading && (
                  <div className='position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-white bg-opacity-75' style={{ zIndex: 100 }}>
                    <div className='spinner-border text-primary' role='status'>
                      <span className='visually-hidden'>Loading...</span>
                    </div>
                  </div>
                )}
                {modalMode === 'view' ? (
                  <div className='p-2'>
                    <div className='mb-6'>
                      <h6 className='text-primary fw-bold mb-4 border-bottom pb-2'>General Information</h6>
                      <div className='row'>
                        {renderViewField('Vessel', form.vesselName)}
                        {renderViewField('Month / Year', form.reportYear && form.reportMonth ? MONTHS_SHORT[form.reportMonth - 1] + ' ' + form.reportYear : '-')}
                      </div>
                    </div>

                    {Array.from(effectiveSections.entries()).map(([sectionName, fields]) => (
                      <div className='mb-6' key={sectionName || '__root__'}>
                        <h6 className='text-primary fw-bold mb-4 border-bottom pb-2'>{sectionName || 'Additional Details'}</h6>
                        <div className='row'>{fields.map(renderFormField)}</div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <>
                    {!isReadOnly && addReportOptions.length > 0 && (
                      <>
                        <div className='mb-4'>
                          <label className='form-label fw-semibold fs-7 text-dark mb-2 d-block'>
                            Report Type
                          </label>
                          <div className='d-flex flex-wrap gap-2'>
                            {addReportOptions.map((report) => {
                              const isActive =
                                modalMode === 'add' ? modalConfig.reportKey === report.key : config.reportKey === report.key
                              return (
                                <button
                                  key={report.key}
                                  type='button'
                                  className={`btn btn-sm d-inline-flex align-items-center gap-2 px-3 py-2 rounded-2 ${isActive
                                    ? 'btn_primary'
                                    : 'btn-light border border-secondary text-dark'
                                    }`}
                                  onClick={() => {
                                    const currentReportKey = modalMode === 'add' ? modalConfig.reportKey : config.reportKey
                                    if (report.key === currentReportKey) return
                                    if (modalMode === 'add') {
                                      setWizardData((prev) => ({
                                        ...prev,
                                        [currentReportKey]: form,
                                      }))
                                    }
                                    onSwitchAddReport?.(report.key, {
                                      vesselId: form.vesselId ?? null,
                                      reportYear: form.reportYear ?? null,
                                      reportMonth: form.reportMonth ?? null,
                                    })
                                  }}
                                >
                                  <i className={`bi ${report.icon} fs-6`}></i>
                                  <span>{report.label}</span>
                                </button>
                              )
                            })}
                          </div>
                        </div>
                        <div className='separator my-4'></div>
                      </>
                    )}

                    {isGlobalScope && modalMode === 'add' && (
                      <div className='row'>
                        <div className={showModalSubCompany ? 'col-md-6 mb-4' : 'col-md-12 mb-4'}>
                          <label className='form-label fw-semibold fs-7 text-dark'>Company</label>
                          <SearchableSelect
                            className='form-select form-select-sm text-dark'
                            style={{ backgroundColor: '#fff' }}
                            value={modalCompanyGroup}
                            onChange={async (e) => {
                              const next = e.target.value
                              setModalCompanyGroup(next)
                              setModalCompanyAdmin('')
                              const nv = getModalFilteredVessels(next, '')
                              const nextVesselId = nv[0]?.id ? Number(nv[0].id) : null
                              if (nextVesselId && modalMode === 'add') {
                                const preferredMonthParts = {
                                  reportYear: form.reportYear,
                                  reportMonth: form.reportMonth
                                }
                                const withDefaults = await applyVesselDefaults(nextVesselId, preferredMonthParts, form)
                                setForm(withDefaults)
                              } else {
                                setForm((prev) => ({
                                  ...prev,
                                  vesselId: nextVesselId,
                                }))
                              }
                            }}
                          >
                            <option value=''>All Companies</option>
                            {companyAdmins.map((c) => (
                              <option key={c.id} value={c.id}>
                                {c.name}
                              </option>
                            ))}
                          </SearchableSelect>
                        </div>
                        {showModalSubCompany && (
                          <div className='col-md-6 mb-4'>
                            <label className='form-label fw-semibold fs-7 text-dark'>Sub-Company</label>
                            <SearchableSelect
                              className='form-select form-select-sm text-dark'
                              style={{ backgroundColor: '#fff' }}
                              value={modalCompanyAdmin}
                              onChange={async (e) => {
                                const next = e.target.value
                                setModalCompanyAdmin(next)
                                const nv = getModalFilteredVessels(modalCompanyGroup, next)
                                const nextVesselId = nv[0]?.id ? Number(nv[0].id) : null
                                if (nextVesselId && modalMode === 'add') {
                                  const preferredMonthParts = {
                                    reportYear: form.reportYear,
                                    reportMonth: form.reportMonth
                                  }
                                  const withDefaults = await applyVesselDefaults(nextVesselId, preferredMonthParts, form)
                                  setForm(withDefaults)
                                } else {
                                  setForm((prev) => ({
                                    ...prev,
                                    vesselId: nextVesselId,
                                  }))
                                }
                              }}
                            >
                              <option value=''>All Sub-Companies</option>
                              {modalSubCompanies.map((c) => (
                                <option key={c.id} value={c.id}>
                                  {c.name}
                                </option>
                              ))}
                            </SearchableSelect>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Vessel, Month */}
                    <div className='row'>
                      {modalMode === 'add' && !canMultiMonthForShipboard && (
                        <div className='col-md-6 mb-4'>
                          <label className='form-label fw-semibold fs-7 text-dark'>
                            Vessel <span className='text-danger'>*</span>
                          </label>
                          <SearchableSelect
                            className='form-select form-select-sm text-dark'
                            style={{ backgroundColor: '#fff' }}
                            value={form.vesselId ?? ''}
                            disabled={isReadOnly}
                            onChange={async (e) => {
                              const nextVesselId = e.target.value ? Number(e.target.value) : null
                              if (nextVesselId && modalMode === 'add') {
                                await initializeWizardData(nextVesselId, form.reportMonth ?? new Date().getMonth() + 1, form.reportYear ?? currentYear)
                              } else {
                                setField('vesselId', nextVesselId)
                              }
                            }}
                          >
                            <option value=''>Select Vessel</option>
                            {vesselOptions.map((v: any) => (
                              <option key={v.id} value={v.id}>
                                {v.fleet_name || v.name}
                              </option>
                            ))}
                          </SearchableSelect>
                        </div>
                      )}
                      <div className={`${modalMode !== 'add' || canMultiMonthForShipboard ? 'col-md-12' : 'col-md-6'} mb-4`}>
                        <label className='form-label fw-semibold fs-7 text-dark'>
                          Month <span className='text-danger'>*</span>
                        </label>
                        <input
                          type='month'
                          className='form-control form-control-sm text-dark'
                          style={{ backgroundColor: '#fff' }}
                          max={canMultiMonthForShipboard ? `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}` : undefined}
                          value={
                            form.reportYear && form.reportMonth
                              ? `${form.reportYear}-${String(form.reportMonth).padStart(2, '0')}`
                              : ''
                          }
                          disabled={isReadOnly || (modalMode === 'add' ? modalConfig.reportKey : config.reportKey) !== 'auxiliary-engine'}
                          onChange={async (e) => {
                            if (!e.target.value) {
                              setField('reportMonth', null)
                              setField('reportYear', null)
                            } else {
                              const [y, m] = e.target.value.split('-')
                              const yr = Number(y)
                              const mn = Number(m)
                              if (form.vesselId && modalMode === 'add') {
                                await initializeWizardData(form.vesselId, mn, yr)
                              } else {
                                setField('reportMonth', mn)
                                setField('reportYear', yr)
                              }
                            }
                          }}
                        />
                      </div>
                    </div>

                    {/* Dynamic form fields grouped by section */}
                    {Array.from(effectiveSections.entries()).map(([sectionName, fields]) => (
                      <React.Fragment key={sectionName || '__root__'}>
                        {sectionName && <div className='separator my-4'></div>}
                        {sectionName && <h6 className='fw-bold mb-3'>{sectionName}</h6>}
                        <div className='row'>{fields.map(renderFormField)}</div>
                      </React.Fragment>
                    ))}

                    {/* Office Review (view-only) */}
                    {isReadOnly &&
                      (form.officeRemarks ||
                        form.status === 'SUBMITTED' ||
                        form.status === 'REVIEWED') && (
                        <>
                          <div className='separator my-4'></div>
                          <h6 className='fw-bold mb-3'>Office Review</h6>
                          <div className='mb-2'>
                            <label className='form-label fw-semibold fs-7 text-dark'>
                              Office Remarks
                            </label>
                            <StructuredRemarksView text={form.officeRemarks} emptyMessage='No office remarks provided.' />
                          </div>
                        </>
                      )}
                  </>
                )}
              </div>
              <div className='modal-footer'>
                {modalMode === 'add' ? (
                  <>
                    {(() => {
                      const activeIdx = addReportOptions.findIndex((r) => r.key === modalConfig.reportKey)
                      return (
                        <>
                          <button type='button' className='btn btn-sm btn-light me-auto' onClick={() => setModalMode(null)}>
                            Cancel
                          </button>
                          {activeIdx > 0 && (
                            <button type='button' className='btn btn-sm btn-secondary me-2' onClick={handlePrevious}>
                              <i className='bi bi-arrow-left me-1'></i>
                              Previous
                            </button>
                          )}
                          {activeIdx < addReportOptions.length - 1 ? (
                            <button type='button' className='btn btn-sm btn_primary' onClick={handleNext}>
                              Next
                              <i className='bi bi-arrow-right ms-1'></i>
                            </button>
                          ) : (
                            <button type='button' className='btn btn-sm btn-success' onClick={handleSave}>
                              <i className='bi bi-check-lg me-1'></i>
                              Save
                            </button>
                          )}
                        </>
                      )
                    })()}
                  </>
                ) : (
                  <>
                    <button className='btn btn-sm btn-light' onClick={() => setModalMode(null)}>
                      {isReadOnly ? 'Close' : 'Cancel'}
                    </button>
                    {!isReadOnly && (
                      <button className='btn btn-sm btn_primary' onClick={handleSave}>
                        <i className='bi bi-check-lg me-1'></i>
                        Update
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <DeleteMainEngineReportModal
        visible={!!deletingRecord}
        onClose={() => setDeletingRecord(null)}
        onConfirm={confirmDelete}
      />

      <MainEngineActionConfirmModal
        visible={!!submitTargetId}
        title='Submit this report to Office?'
        subtitle='You can still review it from office workflow after submission.'
        confirmLabel='Yes, Submit'
        confirmClassName='btn btn_primary'
        onClose={() => setSubmitTargetId(null)}
        onConfirm={confirmSubmit}
      />

      <MainEngineReviewModal
        visible={!!reviewingRecord}
        record={reviewingRecord}
        isLoading={isReviewSaving}
        onClose={handleCloseReviewModal}
        onSave={handleSaveReviewOnly}
        onCloseSatisfactory={handleCloseSatisfactory}
      />
    </>
  )
}

export default MachineryReportTable
